/**
 * Finanzas personales Oruon (Ana) — SOLO super admin.
 * Panel de actualidad: libro + recurrentes + MercadoPago (sin Stripe).
 * Diario vía API externa (/api/finanzas/v1) se proyecta al libro sin exponer
 * el nombre del integrador en UI. Conteos de días con "hoy" real.
 */
import { useEffect, useMemo, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";
import { Navigate } from "react-router-dom";
import {
  AlertTriangle, ArrowDownLeft, ArrowUpRight, Calendar, CalendarClock, CheckCircle2,
  ChevronLeft, ChevronRight, Clock, CreditCard, DollarSign, Loader2, Pencil, Plus, Power,
  RefreshCw, Repeat, Save, Search, Trash2, TrendingDown, TrendingUp, Wallet, X,
} from "lucide-react";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, ComposedChart, Legend, Line,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { AdminKpiGrid } from "@/components/admin/AdminKpiGrid";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { AdminStatCard } from "@/components/admin/AdminStatCard";
import { AdminUpcomingList, type AdminUpcomingItem } from "@/components/admin/AdminUpcomingList";
import { AdminUtilizationBar } from "@/components/admin/AdminUtilizationBar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useUser } from "@clerk/clerk-react";
import { useIsSuperAdmin } from "@/lib/auth";
import {
  useDolarOficial,
  useFinanzasPersonales,
  useRefreshFinanzas,
  useSaveFinanzas,
} from "@/hooks/useFinanzasPersonales";
import {
  countMpExtractRows,
  daysUntil, fmt, fmtCompact, fmtMoney, fmtNum, formatTodayEsAR, FREQUENCY_LABELS, inCurrency,
  isRecurringPendingValidation, itemLedgerDate, monthlyEquivalent, nextOccurrence, occurrencesInMonth, RECURRING_CATEGORIES, recurringCategoryMeta,
  ALL_CATEGORIES, GASTOS_FRECUENTES_CATALOG, LEDGER_ITEM_SECTIONS, SECTION_LABELS, sectionLabelForToday, sumActive, TCOLOR, TX_CATEGORY_META, txCategoryEmoji,
  type FinanzasAccountTx,
  type FinanzasCategory, type FinanzasCurrency, type FinanzasData, type FinanzasHistoryRow, type FinanzasItem,
  type FinanzasLoanRow, type FinanzasMeta, type FinanzasMrr, type FinanzasRealTx, type FinanzasRecurring, type FinanzasSection,
  type FinanzasStatus, type FinanzasTransaction, type RecurringFrequency, type TxKind,
  type MoneyInDetail, type WalletMonthRollup, type WalletOutflowTrace,
} from "@/lib/finanzasFormat";
import { cn } from "@/lib/utils";

const CATEGORIES: FinanzasCategory[] = ALL_CATEGORIES;
const STATUSES: FinanzasStatus[] = ["pending", "paid", "mora", "cancelled"];
const inputCls =
  "w-full rounded-md border border-white/10 bg-white/[0.04] px-2 py-1 text-[13px] text-white/90 outline-none focus:border-white/25";

function catColor(c: FinanzasCategory | null): string {
  if (!c) return "#94a3b8";
  return (TCOLOR as Record<string, string>)[c] ?? "#94a3b8";
}
const itemsOf = (data: { items?: FinanzasItem[] } | undefined, s: FinanzasSection) =>
  (data?.items ?? []).filter((i) => i.section === s);

// ── Oruon (Transacciones + Recurrentes) — helpers compartidos ───────────────────
let _negId = -1;
const nextNegId = () => _negId--;

/** Fecha corta es-AR: "11 jul". */
function fmtShortDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-AR", { day: "numeric", month: "short" }).format(new Date(`${iso}T00:00:00`));
  } catch {
    return iso;
  }
}
const todayISO = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const capitalize = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** Cotización efectiva ARS/USD: dolarapi en vivo → override manual (meta) → ninguna. */
function useFinanzasRate(meta: FinanzasMeta | null | undefined) {
  const { data: dolar, isLoading } = useDolarOficial();
  const live = dolar?.venta ?? null;
  const manual = meta?.usd_ars_rate ?? null;
  const rate = live ?? manual;
  const source: "live" | "manual" | "none" = live != null ? "live" : manual != null ? "manual" : "none";
  return { rate, source, live, manual, isLoading, fecha: dolar?.fecha ?? null };
}

/** Toggle ARS ⇆ USD reutilizable. */
function CurrencyToggle({ value, onChange }: { value: FinanzasCurrency; onChange: (c: FinanzasCurrency) => void }) {
  return (
    <div className="inline-flex overflow-hidden rounded-lg border border-white/10 bg-white/[0.03] text-[12px]" role="group" aria-label="Moneda de visualización">
      {(["ARS", "USD"] as FinanzasCurrency[]).map((c) => (
        <button
          key={c}
          type="button"
          aria-pressed={value === c}
          onClick={() => onChange(c)}
          className={cn("px-2.5 py-1 font-semibold transition", value === c ? "bg-white/15 text-white" : "text-white/50 hover:text-white/80")}
        >
          {c}
        </button>
      ))}
    </div>
  );
}

const EMPTY_REAL_TX: FinanzasRealTx[] = [];
const EMPTY_ACCOUNT_TX: FinanzasAccountTx[] = [];
const EMPTY_MANUAL_TX: FinanzasTransaction[] = [];

function confirmRemove(label: string): boolean {
  return window.confirm(`¿Quitar «${label}»?\nPodés deshacer solo si no guardaste todavía (en Editar) o no se puede deshacer (libro/recurrentes).`);
}

/** Nombre de mes en es-AR (capitalizado) relativo a `today`. */
function monthNameEs(today: Date, offsetMonths = 0): string {
  const d = new Date(today.getFullYear(), today.getMonth() + offsetMonths, 1);
  return capitalize(new Intl.DateTimeFormat("es-AR", { month: "long" }).format(d));
}

function fmtRelativeTime(ts: number | null | undefined): string {
  if (ts == null || !Number.isFinite(ts) || ts <= 0) return "—";
  const sec = Math.round((Date.now() - ts) / 1000);
  if (sec < 10) return "ahora";
  if (sec < 60) return `hace ${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `hace ${h} h`;
  return new Intl.DateTimeFormat("es-AR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).format(new Date(ts));
}

// ── KPIs de actualidad (mes dinámico + snapshot revenue real) ─────────────────
function KpiCards({ data, today }: { data: FinanzasData; today: Date }) {
  const mes = monthNameEs(today, 0);
  const pagado = itemsOf(data, "hoy_pagado").reduce((s, x) => s + (x.amount ?? 0), 0);
  const faltaMes = sumActive(itemsOf(data, "julio"));
  const nPend = itemsOf(data, "julio").filter((x) => x.status !== "paid" && x.status !== "cancelled").length;
  const deuda = itemsOf(data, "deuda").reduce(
    (s, x) => (x.status === "cancelled" ? s : s + (x.amount ?? 0)),
    0,
  );
  const ingresosPrev = sumActive(itemsOf(data, "ingreso"));
  const realN = data.real?.incomeTransactions?.length ?? 0;
  const realBrand = data.real?.brand ?? null;
  const mrr = data.real?.mrr;
  // Valor del KPI = solo planificados (sección ingreso). El feed real se resume en el hint
  // y se detalla en Transacciones (bruto/neto/MRR) para no mezclar dos fuentes en un número.

  return (
    <AdminKpiGrid cols={4}>
      <AdminStatCard
        title="Pagado (cierre)"
        value={fmt(pagado)}
        icon={DollarSign}
        hint={`${itemsOf(data, "hoy_pagado").length} items cerrados`}
        accent
        className="border-emerald-500/20"
      />
      <AdminStatCard
        title={`Falta en ${mes}`}
        value={fmt(faltaMes)}
        icon={CalendarClock}
        hint={`${nPend} vencimientos · plan del mes`}
        className="border-amber-500/20"
      />
      <AdminStatCard
        title="Deuda vigente"
        value={fmt(deuda)}
        icon={CreditCard}
        hint="créditos + tarjetas"
        className="border-sky-500/20"
      />
      <AdminStatCard
        title="Ingresos previstos"
        value={fmt(ingresosPrev)}
        icon={TrendingUp}
        hint={
          realN > 0
            ? `${itemsOf(data, "ingreso").length} planificados · feed MP ${realN} tx${mrr && mrr.activeCount > 0 ? ` · MRR ${mrr.activeCount}` : ""}${realBrand ? ` · ${realBrand}` : ""} · ver Transacciones`
            : ingresosPrev > 0
              ? `${itemsOf(data, "ingreso").length} cobros en plan · sin feed real en ventana`
              : "Sin plan · feed real (si hay) en Transacciones"
        }
        className="border-violet-500/20"
      />
    </AdminKpiGrid>
  );
}

/** Cobertura del plan del mes: pagado / (pagado + falta). */
function PlanCoverageBar({ data }: { data: FinanzasData }) {
  const pagado = itemsOf(data, "hoy_pagado").reduce((s, x) => s + (x.amount ?? 0), 0);
  const falta = sumActive(itemsOf(data, "julio"));
  const base = pagado + falta;
  if (base <= 0) return null;
  return (
    <AdminUtilizationBar
      label="Cobertura del plan (pagado vs pendiente del mes)"
      value={pagado}
      max={base}
      higherIsBetter
      hint={`${fmt(pagado)} pagado · ${fmt(falta)} falta`}
      formatValue={(n) => fmt(n)}
    />
  );
}

/** Barra de actualidad: frescura del query, cotización, feed real, refresh. */
function FinanzasActualidadBar({
  data,
  dataUpdatedAt,
  isFetching,
  onRefresh,
}: {
  data: FinanzasData;
  dataUpdatedAt: number;
  isFetching: boolean;
  onRefresh: () => void;
}) {
  const { rate, source, fecha } = useFinanzasRate(data.meta);
  const realAt = data.real?.generatedAt ? Date.parse(data.real.generatedAt) : null;
  const realN = data.real?.incomeTransactions?.length ?? 0;
  const mpN = data.real?.incomeTransactions?.length ?? 0;
  const mrr = data.real?.mrr;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/[0.08] bg-white/[0.02] px-3 py-2.5">
      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-white/45">
        <span className="inline-flex items-center gap-1.5 font-semibold uppercase tracking-wide text-white/55">
          <span className={cn("h-1.5 w-1.5 rounded-full", isFetching ? "animate-pulse bg-amber-400" : "bg-emerald-400/90")} />
          Actualidad
        </span>
        <span className="tabular-nums">Panel {fmtRelativeTime(dataUpdatedAt)}</span>
        {realAt != null && Number.isFinite(realAt) && (
          <span className="tabular-nums" title={data.real?.generatedAt ?? undefined}>
            Feed MP {fmtRelativeTime(realAt)}
            {realN > 0 && (
              <span className="text-white/35"> · {mpN} cobros MP</span>
            )}
          </span>
        )}
        {mrr && mrr.activeCount > 0 && (
          <span className="text-violet-300/80">
            MRR MP {mrr.activeCount} activa{mrr.activeCount === 1 ? "" : "s"}
          </span>
        )}
        {rate != null ? (
          <span className="tabular-nums">
            USD {fmtNum(rate)} ARS
            <span className="text-white/30"> · {source === "live" ? "dólar en vivo" : "override"}</span>
            {source === "live" && fecha ? (
              <span className="text-white/25"> · {new Date(fecha).toLocaleString("es-AR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
            ) : null}
          </span>
        ) : (
          <span className="text-amber-300/70">Sin cotización USD</span>
        )}
        {data.real?.brand && (
          <span className="rounded-full bg-white/[0.06] px-1.5 py-0.5 text-[10px] text-white/40">{data.real.brand}</span>
        )}
        {data.real?.mpFilter?.note && (
          <span className="max-w-xl text-[10px] text-white/30" title={data.real.mpFilter.note}>
            MP: {data.real.mpFilter.businessIncomeCount} cobros marca
            <span className="text-white/25"> · sin Stripe · sin transferencias en ingresos</span>
          </span>
        )}
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={isFetching}
        onClick={onRefresh}
        className="h-8 shrink-0 gap-1.5 border-white/10 text-[12px]"
        title="Refrescar panel y feed MercadoPago"
      >
        <RefreshCw className={cn("h-3.5 w-3.5", isFetching && "animate-spin")} />
        {isFetching ? "Actualizando…" : "Actualizar"}
      </Button>
    </div>
  );
}

// ── Day badge (dinámico) ────────────────────────────────────────────────────────
function DayBadge({ item, today }: { item: FinanzasItem; today: Date }) {
  const d = daysUntil(item.due_date, today);
  const paid = item.status === "paid" || item.status === "cancelled";
  if (paid) return null;
  const overdue = (d != null && d < 0) || item.status === "mora";
  if (overdue) {
    return (
      <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-[10px] font-bold text-red-400">
        {item.status === "mora" ? "⚠ EN MORA" : `vencido hace ${Math.abs(d ?? 0)}d`}
      </span>
    );
  }
  if (d == null) return <span className="text-[10px] text-white/35">estimado</span>;
  return (
    <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-bold", d <= 14 ? "bg-amber-500/15 text-amber-400" : "bg-sky-500/15 text-sky-300")}>
      {d === 0 ? "vence hoy" : d === 1 ? "mañana" : `en ${d} días`}
    </span>
  );
}

// ── VIEW: Lista ─────────────────────────────────────────────────────────────────
function ListaTab({ data, today }: { data: FinanzasData; today: Date }) {
  const recon = itemsOf(data, "reconcile");
  const notIn = itemsOf(data, "not_in_list");
  const pagado = itemsOf(data, "hoy_pagado");
  return (
    <div className="space-y-4">
      <Card className="rounded-2xl border-white/[0.07] bg-white/[0.02] shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Reconciliación — lista vs. realidad</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {recon.length === 0 && (
            <p className="rounded-lg border border-dashed border-white/10 px-3 py-4 text-center text-[12px] text-white/35">
              Sin filas de reconciliación. Usá <span className="text-white/55">Editar</span> para cargar el plan.
            </p>
          )}
          {recon.map((r) => {
            const paid = r.status === "paid";
            return (
              <div key={r.id} className={cn("rounded-lg border p-3", paid ? "border-emerald-600/40 bg-emerald-500/[0.06]" : "border-white/10 bg-white/[0.02]")}>
                <div className="flex items-start gap-2">
                  <span className="text-lg leading-none">{paid ? "✅" : "⏳"}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="text-[13px] font-semibold text-white/90">
                        {r.label} <span className="ml-1 text-[11px] font-normal text-white/40">vence {r.due_label ?? "—"}</span>
                      </div>
                      <div className="flex items-center gap-3 text-right">
                        <div>
                          <div className="text-[9px] text-white/40">Lista</div>
                          <div className={cn("text-[12px]", paid && "text-white/35 line-through")}>{fmt(r.amount_planned)}</div>
                        </div>
                        {r.amount != null && (
                          <div>
                            <div className="text-[9px] text-white/40">Real</div>
                            <div className={cn("text-[13px] font-bold", paid ? "text-emerald-400" : "text-amber-400")}>{fmt(r.amount)}</div>
                          </div>
                        )}
                      </div>
                    </div>
                    {r.note && <div className="mt-1 text-[11px] text-white/45">{r.note}</div>}
                  </div>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-amber-500/20 bg-white/[0.02] shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-amber-400">⚠ En las fechas pero NO en la lista</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {notIn.length === 0 && (
            <p className="rounded-lg border border-dashed border-white/10 px-3 py-4 text-center text-[12px] text-white/35">Nada fuera de lista por ahora.</p>
          )}
          {notIn.map((x) => (
            <div key={x.id} className="flex items-start justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.02] p-2.5">
              <div className="flex min-w-0 items-start gap-2">
                <span>{x.status === "mora" ? "🔴" : "🟡"}</span>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={cn("text-[13px] font-semibold", x.status === "mora" ? "text-red-400" : "text-amber-300")}>{x.label}</span>
                    <span className="text-[10px] text-white/40">vence {x.due_label ?? "—"}</span>
                    <DayBadge item={x} today={today} />
                  </div>
                  {x.note && <div className="mt-0.5 text-[11px] text-white/45">{x.note}</div>}
                </div>
              </div>
              <span className={cn("shrink-0 proportional-nums tracking-tight text-[13px] font-bold", x.status === "mora" ? "text-red-400" : "text-amber-300")}>{fmt(x.amount)}</span>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-emerald-600/30 bg-white/[0.02] shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-emerald-400">✅ Pagado — {fmt(pagado.reduce((s, x) => s + (x.amount ?? 0), 0))}</CardTitle></CardHeader>
        <CardContent className="space-y-1.5">
          {pagado.map((p) => (
            <div key={p.id} className="flex items-center gap-2.5 rounded-lg border border-emerald-600/30 bg-emerald-500/[0.06] p-2">
              <span className="text-base">{p.icon ?? "•"}</span>
              <div className="min-w-0 flex-1">
                <div className="text-[12px] font-medium text-emerald-300">{p.label}</div>
                {p.note && <div className="text-[10px] text-white/40">{p.note}</div>}
              </div>
              <span className="shrink-0 proportional-nums tracking-tight text-[13px] font-bold text-emerald-400">{fmt(p.amount)}</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// ── VIEW: Mes (sección DB "julio" / "agosto" — labels dinámicos por hoy) ────────
function JulioTab({ data, today }: { data: FinanzasData; today: Date }) {
  const mes = monthNameEs(today, 0);
  const mesProx = monthNameEs(today, 1);
  const julio = itemsOf(data, "julio");
  const agosto = itemsOf(data, "agosto");
  const totalJulio = sumActive(julio);
  const totalAgosto = agosto.reduce((s, x) => (x.status === "cancelled" ? s : s + (x.amount ?? 0)), 0);
  return (
    <div className="space-y-4">
      <Card className="rounded-2xl border-white/[0.07] bg-white/[0.02] shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Calendario {mes} — pendientes</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {julio.length === 0 && (
            <p className="rounded-lg border border-dashed border-white/10 px-3 py-5 text-center text-[12px] text-white/35">
              Sin pendientes en el plan del mes. Cargalos en <span className="text-white/55">Editar</span> o en Recurrentes.
            </p>
          )}
          {julio.map((p) => (
            <div key={p.id} className={cn("rounded-lg border p-3", p.status === "mora" ? "border-red-600/40 bg-red-500/[0.06]" : (daysUntil(p.due_date, today) ?? 99) <= 14 ? "border-amber-500/30 bg-amber-500/[0.04]" : "border-white/10 bg-white/[0.02]")}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <span className="proportional-nums tracking-tight text-[12px] font-bold text-white/80">{p.due_label ?? p.due_date}</span>
                    <DayBadge item={p} today={today} />
                  </div>
                  <div className="text-[13px] font-semibold text-white/90">{p.label}</div>
                  {p.note && <div className="mt-0.5 text-[10px] text-white/40">{p.note}</div>}
                </div>
                <span className="shrink-0 proportional-nums tracking-tight text-[15px] font-extrabold" style={{ color: catColor(p.category) }}>{fmt(p.amount)}</span>
              </div>
            </div>
          ))}
          <div className="flex items-center justify-between border-t border-white/10 pt-2.5">
            <span className="text-[12px] font-bold text-white/80">Total {mes} pendiente</span>
            <span className="proportional-nums tracking-tight text-[16px] font-extrabold text-red-400">{fmt(totalJulio)}</span>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-sky-500/20 bg-white/[0.02] shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-sky-300">{capitalize(mesProx)} — lo que se viene</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {agosto.map((p) => (
            <div key={p.id} className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <span className="mr-2 proportional-nums tracking-tight text-[11px] text-sky-300">{p.due_label ?? p.due_date}</span>
                <span className="text-[12px] text-white/85">{p.label}</span>
                {p.note && <div className="text-[10px] text-white/40">{p.note}</div>}
              </div>
              <span className="shrink-0 proportional-nums tracking-tight text-[13px] font-bold" style={{ color: catColor(p.category) }}>{fmt(p.amount)}</span>
            </div>
          ))}
          <div className="flex items-center justify-between border-t border-white/10 pt-2">
            <span className="text-[11px] font-bold text-white/70">Total {mesProx} est.</span>
            <span className="proportional-nums tracking-tight text-[14px] font-extrabold text-sky-300">{fmt(totalAgosto)}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── VIEW: Deuda ─────────────────────────────────────────────────────────────────
function DeudaTab({ data }: { data: FinanzasData }) {
  const deuda = itemsOf(data, "deuda");
  const total = deuda.reduce((s, x) => (x.status === "cancelled" ? s : s + (x.amount ?? 0)), 0);
  const maxA = Math.max(1, ...deuda.map((d) => d.amount ?? 0));
  const m = data.meta;
  const chartData = data.history.map((h) => ({ mes: h.month_label ?? "", NaranjaX: h.naranjax ?? 0, "Ualá": h.uala ?? 0 }));
  return (
    <div className="space-y-4">
      <Card className="rounded-2xl border-sky-500/20 bg-white/[0.02] shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-sky-300">Deuda vigente — {fmt(total)}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {deuda.map((d) => (
            <div key={d.id} className={cn("flex items-center gap-3", d.status === "cancelled" && "opacity-40")}>
              <div className="min-w-0 flex-1">
                <div className={cn("text-[12px] text-white/85", d.status === "cancelled" && "line-through")}>{d.label}</div>
                {d.note && <div className="text-[10px] text-white/40">{d.note}</div>}
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/10">
                  <div className="h-full rounded-full" style={{ width: `${Math.round(((d.amount ?? 0) / maxA) * 100)}%`, background: catColor(d.category) }} />
                </div>
              </div>
              <span className="w-20 shrink-0 text-right proportional-nums tracking-tight text-[13px] font-bold" style={{ color: catColor(d.category) }}>{fmt(d.amount)}</span>
            </div>
          ))}
        </CardContent>
      </Card>

      {chartData.length > 0 && (
        <Card className="rounded-2xl border-white/[0.07] bg-white/[0.02] shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm">📊 Gastos tarjetas</CardTitle></CardHeader>
          <CardContent>
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                  <XAxis dataKey="mes" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} />
                  <YAxis tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 9 }} tickFormatter={(v) => (v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`)} />
                  <Tooltip formatter={(v: number, n) => [fmt(v), n]} contentStyle={{ background: "#0b0b0b", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }} />
                  <Bar dataKey="NaranjaX" fill="#e84393" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Ualá" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="rounded-2xl border-white/[0.07] bg-white/[0.02] shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Préstamo Ualá — 12 cuotas {m && !m.loan_active && <span className="ml-2 rounded bg-red-500/15 px-2 py-0.5 text-[10px] text-red-400">CANCELADO</span>}</CardTitle></CardHeader>
        <CardContent>
          {m && (
            <div className="mb-3 rounded-lg border border-amber-500/20 bg-amber-500/[0.04] p-2.5 text-[11px] leading-relaxed text-amber-200/80">
              Capital {fmt(m.loan_capital)} · Neto {fmt(m.loan_neto)} · Total a devolver {fmt(m.loan_total)} · TNA ≈ {m.loan_tna} · 1ª cuota: {m.loan_first_date ?? "—"}
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-white/40">
                  {["N°", "Mes", "Capital", "Interés+IVA", "Total", "% int."].map((h, i) => (
                    <th key={h} className={cn("py-1 font-medium", i <= 1 ? "text-left" : "text-right")}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {data.loan.map((c) => {
                  const tot = (c.capital ?? 0) + (c.interest ?? 0);
                  const ip = tot ? ((c.interest ?? 0) / tot) * 100 : 0;
                  return (
                    <tr key={c.id} className="border-t border-white/[0.05]">
                      <td className="py-1 text-center text-white/40">{c.cuota_n}</td>
                      <td className="py-1 text-left font-sans text-white/55">{c.period_label}</td>
                      <td className="py-1 text-right text-white/70">{fmt(c.capital, 2)}</td>
                      <td className="py-1 text-right text-red-400">{fmt(c.interest, 2)}</td>
                      <td className="py-1 text-right font-semibold text-white/85">{fmt(tot, 2)}</td>
                      <td className={cn("py-1 text-right", ip > 70 ? "text-red-400" : ip > 50 ? "text-amber-400" : "text-emerald-400")}>{ip.toFixed(0)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── VIEW: Evolución (charts profesionales) ───────────────────────────────────────
function EvolucionTab({ data, today }: { data: FinanzasData; today: Date }) {
  const m = data.meta;
  const ingresos = itemsOf(data, "ingreso");
  const tooltipStyle = { background: "#0b0b0b", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 } as const;

  // Cómo se cancela el préstamo: saldo a lo largo de las cuotas (incluye punto inicial = capital).
  const loanArea = [
    ...(m?.loan_capital != null && data.loan.length ? [{ mes: "Inicio", saldo: m.loan_capital }] : []),
    ...data.loan.map((c) => ({ mes: c.period_label ?? `#${c.cuota_n}`, saldo: c.balance ?? 0 })),
  ];

  // Flujo de caja: buckets del plan (hoy_pagado / julio / agosto) con labels del mes actual.
  const shortMes = (offset: number) => {
    const d = new Date(today.getFullYear(), today.getMonth() + offset, 1);
    return capitalize(new Intl.DateTimeFormat("es-AR", { month: "short" }).format(d).replace(".", ""));
  };
  const ymPrefix = (offset: number) => {
    const d = new Date(today.getFullYear(), today.getMonth() + offset, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  };
  const ingMonth = (p: string) =>
    ingresos.filter((i) => (i.due_date ?? "").startsWith(p) && i.status !== "cancelled").reduce((s, x) => s + (x.amount ?? 0), 0);
  const egActive = (sec: FinanzasSection) => itemsOf(data, sec).reduce((s, x) => (x.status === "cancelled" ? s : s + (x.amount ?? 0)), 0);
  // Mapeo fijo de secciones del plan → mes relativo (cierre / actual / próximo).
  const cashflow = [
    { mes: shortMes(-1), egresos: egActive("hoy_pagado"), ingresos: ingMonth(ymPrefix(-1)) },
    { mes: shortMes(0), egresos: sumActive(itemsOf(data, "julio")), ingresos: ingMonth(ymPrefix(0)) },
    { mes: shortMes(1), egresos: egActive("agosto"), ingresos: ingMonth(ymPrefix(1)) },
  ].map((r) => ({ ...r, neto: r.ingresos - r.egresos }));
  const ingresosTot = sumActive(ingresos);

  return (
    <div className="space-y-4">
      <Card className="rounded-2xl border-white/[0.07] bg-white/[0.02] shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm">📉 Cómo se cancela el préstamo (saldo proyectado)</CardTitle></CardHeader>
        <CardContent>
          {m && (
            <div className="mb-2 text-[11px] text-white/45">
              Total a devolver {fmt(m.loan_total)} · 1ª cuota {m.loan_first_date ?? "—"} · llega a $0 en la cuota 12.
            </div>
          )}
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={loanArea} margin={{ top: 4, right: 8, left: -6, bottom: 0 }}>
                <defs>
                  <linearGradient id="finSaldo" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#38bdf8" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#38bdf8" stopOpacity={0.03} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="mes" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 9 }} interval={0} angle={-30} textAnchor="end" height={42} />
                <YAxis tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 9 }} tickFormatter={(v) => (v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`)} />
                <Tooltip formatter={(v: number) => [fmt(v), "Saldo"]} contentStyle={tooltipStyle} />
                <Area type="monotone" dataKey="saldo" stroke="#38bdf8" strokeWidth={2} fill="url(#finSaldo)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-white/[0.07] bg-white/[0.02] shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm">💸 Flujo de caja por mes — ingresos vs. egresos</CardTitle></CardHeader>
        <CardContent>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={cashflow} margin={{ top: 4, right: 8, left: -6, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="mes" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} />
                <YAxis tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 9 }} tickFormatter={(v) => (v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`)} />
                <Tooltip formatter={(v: number, n) => [fmt(v), n]} contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="ingresos" name="Ingresos" fill="#10b981" radius={[3, 3, 0, 0]} />
                <Bar dataKey="egresos" name="Egresos" fill="#ef4444" radius={[3, 3, 0, 0]} />
                <Line type="monotone" dataKey="neto" name="Neto" stroke="#a78bfa" strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 grid grid-cols-3 gap-2">
            {cashflow.map((c) => (
              <div key={c.mes} className="rounded-lg bg-white/[0.03] p-2 text-center">
                <div className="text-[10px] text-white/40">{c.mes} · neto</div>
                <div className={cn("proportional-nums tracking-tight text-[13px] font-bold", c.neto >= 0 ? "text-emerald-400" : "text-red-400")}>{c.neto >= 0 ? "+" : "-"}{fmt(Math.abs(c.neto))}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-violet-500/20 bg-white/[0.02] shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-violet-300">💵 Ingresos {ingresosTot > 0 ? `— ${fmt(ingresosTot)}` : ""}</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {ingresos.length === 0 ? (
            <div className="rounded-lg border border-dashed border-white/10 p-4 text-center text-[12px] text-white/40">
              Sin ingresos cargados. Agregalos en <span className="text-white/70">Editar → Ingresos</span> para proyectar el flujo de caja y ver si alcanza para ir cancelando deuda.
            </div>
          ) : (
            ingresos.map((it) => (
              <div key={it.id} className={cn("flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/[0.02] p-2.5", it.status === "cancelled" && "opacity-40")}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-medium text-white/85">{it.label}</span>
                    {it.due_label && <span className="text-[10px] text-white/40">{it.due_label}</span>}
                    {it.status === "paid" && <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] text-emerald-400">recibido</span>}
                  </div>
                  {it.note && <div className="text-[10px] text-white/40">{it.note}</div>}
                </div>
                <span className="shrink-0 proportional-nums tracking-tight text-[13px] font-bold text-emerald-400">{fmt(it.amount)}</span>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── EDIT ─────────────────────────────────────────────────────────────────────────
type Draft = {
  meta: FinanzasMeta;
  items: FinanzasItem[];
  loan: FinanzasLoanRow[];
  history: FinanzasHistoryRow[];
};
let tempId = -1;
const nextTempId = () => tempId--;

function EditView({
  draft, setDraft, removed, setRemoved, today,
}: {
  draft: Draft;
  setDraft: Dispatch<SetStateAction<Draft | null>>;
  removed: { items: number[]; loan: number[]; history: number[] };
  setRemoved: Dispatch<SetStateAction<{ items: number[]; loan: number[]; history: number[] }>>;
  today: Date;
}) {
  const upItem = (id: number, patch: Partial<FinanzasItem>) =>
    setDraft((d) => (d ? { ...d, items: d.items.map((i) => (i.id === id ? { ...i, ...patch } : i)) } : d));
  const addItem = (section: FinanzasSection) =>
    setDraft((d) => {
      if (!d) return d;
      return {
        ...d,
        items: [...d.items, {
          id: nextTempId(), section, label: "", category: null, amount: null, amount_planned: null,
          due_date: null, due_label: null, status: "pending", icon: null, note: null,
          sort_order: itemsOf(d, section).length,
        }],
      };
    });
  const delItem = (it: FinanzasItem) => {
    if (!confirmRemove(it.label || "fila")) return;
    setDraft((d) => (d ? { ...d, items: d.items.filter((i) => i.id !== it.id) } : d));
    if (it.id > 0) setRemoved((r) => ({ ...r, items: [...r.items, it.id] }));
  };
  const patchMeta = (patch: Partial<FinanzasMeta>) =>
    setDraft((d) => (d ? { ...d, meta: { ...d.meta, ...patch } } : d));

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.06] px-3 py-2 text-[12px] text-amber-100/80">
        Edición del plan (meta, items, préstamo, historial). Guardá en el header. Cambios no guardados se pierden al cancelar.
      </div>
      {/* Meta */}
      <Card className="rounded-2xl border-white/[0.07] bg-white/[0.02] shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Datos generales · préstamo · cotización</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {([["person_name", "Nombre"], ["gestor", "Gestor"], ["alert_text", "Alerta (título)"], ["alert_subtext", "Alerta (subtítulo)"], ["loan_tna", "TNA préstamo"]] as const).map(([k, lbl]) => (
            <label key={k} className="text-[11px] text-white/50">{lbl}
              <input className={inputCls} value={(draft.meta[k] as string) ?? ""} onChange={(e) => patchMeta({ [k]: e.target.value })} />
            </label>
          ))}
          {([["loan_capital", "Capital"], ["loan_neto", "Neto recibido"], ["loan_total", "Total a devolver"]] as const).map(([k, lbl]) => (
            <label key={k} className="text-[11px] text-white/50">{lbl}
              <input type="number" className={inputCls} value={(draft.meta[k] as number) ?? ""} onChange={(e) => patchMeta({ [k]: e.target.value === "" ? null : Number(e.target.value) })} />
            </label>
          ))}
          <label className="text-[11px] text-white/50">1ª cuota
            <input type="date" className={inputCls} value={draft.meta.loan_first_date ?? ""} onChange={(e) => patchMeta({ loan_first_date: e.target.value || null })} />
          </label>
          <label className="text-[11px] text-white/50">
            USD/ARS override (si falla dolarapi)
            <input
              type="number"
              inputMode="decimal"
              min={1}
              step={1}
              className={inputCls}
              placeholder="Ej. 1420"
              value={draft.meta.usd_ars_rate ?? ""}
              onChange={(e) => patchMeta({ usd_ars_rate: e.target.value === "" ? null : Number(e.target.value) })}
            />
            <span className="mt-0.5 block text-[10px] text-white/35">Vacío = solo cotización en vivo. Se usa en Transacciones/Recurrentes al mostrar USD.</span>
          </label>
          <label className="flex items-center gap-2 pt-5 text-[12px] text-white/70">
            <input type="checkbox" checked={draft.meta.loan_active} onChange={(e) => patchMeta({ loan_active: e.target.checked })} />
            Préstamo activo (destildar = cancelado)
          </label>
        </CardContent>
      </Card>

      {/* Items por sección */}
      {(Object.keys(SECTION_LABELS) as FinanzasSection[]).map((section) => (
        <Card key={section} className="rounded-2xl border-white/[0.07] bg-white/[0.02] shadow-sm">
          <CardHeader className="flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm">{sectionLabelForToday(section, today)}</CardTitle>
            <Button type="button" size="sm" variant="outline" onClick={() => addItem(section)}><Plus className="mr-1 h-3.5 w-3.5" />Fila</Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {itemsOf(draft, section).length === 0 && (
              <p className="text-[12px] text-white/35">Sin filas en esta sección. Usá “Fila” para agregar.</p>
            )}
            {itemsOf(draft, section).map((it) => (
              <div key={it.id} className="grid grid-cols-12 items-center gap-1.5 rounded-lg border border-white/[0.06] bg-white/[0.02] p-2">
                <input className={cn(inputCls, "col-span-12 sm:col-span-3")} placeholder="Nombre" value={it.label} onChange={(e) => upItem(it.id, { label: e.target.value })} />
                <select className={cn(inputCls, "col-span-4 sm:col-span-1")} value={it.category ?? ""} onChange={(e) => upItem(it.id, { category: (e.target.value || null) as FinanzasCategory | null })}>
                  <option value="">cat</option>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input type="number" className={cn(inputCls, "col-span-4 sm:col-span-2")} placeholder="Monto" value={it.amount ?? ""} onChange={(e) => upItem(it.id, { amount: e.target.value === "" ? null : Number(e.target.value) })} />
                {section === "reconcile" ? (
                  <input type="number" className={cn(inputCls, "col-span-4 sm:col-span-1")} placeholder="Plan" value={it.amount_planned ?? ""} onChange={(e) => upItem(it.id, { amount_planned: e.target.value === "" ? null : Number(e.target.value) })} />
                ) : (
                  <input className={cn(inputCls, "col-span-4 sm:col-span-1")} placeholder="icono" value={it.icon ?? ""} onChange={(e) => upItem(it.id, { icon: e.target.value || null })} />
                )}
                <input type="date" className={cn(inputCls, "col-span-6 sm:col-span-2")} value={it.due_date ?? ""} onChange={(e) => upItem(it.id, { due_date: e.target.value || null })} />
                <select className={cn(inputCls, "col-span-3 sm:col-span-1")} value={it.status ?? "pending"} onChange={(e) => upItem(it.id, { status: e.target.value as FinanzasStatus })}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <input className={cn(inputCls, "col-span-9 sm:col-span-1")} placeholder="Etiqueta fecha" value={it.due_label ?? ""} onChange={(e) => upItem(it.id, { due_label: e.target.value || null })} />
                <button type="button" aria-label={`Eliminar ${it.label || "fila"}`} onClick={() => delItem(it)} className="col-span-3 flex justify-center text-red-400/70 hover:text-red-400 sm:col-span-1"><Trash2 className="h-4 w-4" /></button>
                <input className={cn(inputCls, "col-span-12")} placeholder="Nota" value={it.note ?? ""} onChange={(e) => upItem(it.id, { note: e.target.value || null })} />
              </div>
            ))}
          </CardContent>
        </Card>
      ))}

      {/* Loan */}
      <Card className="rounded-2xl border-white/[0.07] bg-white/[0.02] shadow-sm">
        <CardHeader className="flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm">Cuadro del préstamo</CardTitle>
          <Button type="button" size="sm" variant="outline" onClick={() => setDraft((d) => d ? { ...d, loan: [...d.loan, { id: nextTempId(), cuota_n: d.loan.length + 1, period_label: "", period_date: null, capital: null, interest: null, balance: null, sort_order: d.loan.length }] } : d)}><Plus className="mr-1 h-3.5 w-3.5" />Cuota</Button>
        </CardHeader>
        <CardContent className="space-y-1.5">
          {draft.loan.map((c) => (
            <div key={c.id} className="grid grid-cols-12 gap-1.5">
              <input type="number" className={cn(inputCls, "col-span-2 sm:col-span-1")} value={c.cuota_n} onChange={(e) => setDraft((d) => d ? { ...d, loan: d.loan.map((x) => x.id === c.id ? { ...x, cuota_n: Number(e.target.value) } : x) } : d)} />
              <input className={cn(inputCls, "col-span-3 sm:col-span-2")} placeholder="Mes" value={c.period_label ?? ""} onChange={(e) => setDraft((d) => d ? { ...d, loan: d.loan.map((x) => x.id === c.id ? { ...x, period_label: e.target.value } : x) } : d)} />
              <input type="number" className={cn(inputCls, "col-span-3")} placeholder="Capital" value={c.capital ?? ""} onChange={(e) => setDraft((d) => d ? { ...d, loan: d.loan.map((x) => x.id === c.id ? { ...x, capital: e.target.value === "" ? null : Number(e.target.value) } : x) } : d)} />
              <input type="number" className={cn(inputCls, "col-span-3")} placeholder="Interés" value={c.interest ?? ""} onChange={(e) => setDraft((d) => d ? { ...d, loan: d.loan.map((x) => x.id === c.id ? { ...x, interest: e.target.value === "" ? null : Number(e.target.value) } : x) } : d)} />
              <input type="number" className={cn(inputCls, "col-span-2 sm:col-span-2")} placeholder="Saldo" value={c.balance ?? ""} onChange={(e) => setDraft((d) => d ? { ...d, loan: d.loan.map((x) => x.id === c.id ? { ...x, balance: e.target.value === "" ? null : Number(e.target.value) } : x) } : d)} />
              <button type="button" aria-label={`Eliminar cuota ${c.cuota_n}`} onClick={() => {
                if (!confirmRemove(`cuota ${c.cuota_n}`)) return;
                setDraft((d) => d ? { ...d, loan: d.loan.filter((x) => x.id !== c.id) } : d);
                if (c.id > 0) setRemoved((r) => ({ ...r, loan: [...r.loan, c.id] }));
              }} className="col-span-1 flex justify-center text-red-400/70 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* History */}
      <Card className="rounded-2xl border-white/[0.07] bg-white/[0.02] shadow-sm">
        <CardHeader className="flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm">Historial gastos tarjetas</CardTitle>
          <Button type="button" size="sm" variant="outline" onClick={() => setDraft((d) => d ? { ...d, history: [...d.history, { id: nextTempId(), month_label: "", month_date: null, naranjax: null, uala: null, sort_order: d.history.length }] } : d)}><Plus className="mr-1 h-3.5 w-3.5" />Mes</Button>
        </CardHeader>
        <CardContent className="space-y-1.5">
          {draft.history.map((h) => (
            <div key={h.id} className="grid grid-cols-12 gap-1.5">
              <input className={cn(inputCls, "col-span-4 sm:col-span-3")} placeholder="Mes" value={h.month_label ?? ""} onChange={(e) => setDraft((d) => d ? { ...d, history: d.history.map((x) => x.id === h.id ? { ...x, month_label: e.target.value } : x) } : d)} />
              <input type="number" className={cn(inputCls, "col-span-4")} placeholder="NaranjaX" value={h.naranjax ?? ""} onChange={(e) => setDraft((d) => d ? { ...d, history: d.history.map((x) => x.id === h.id ? { ...x, naranjax: e.target.value === "" ? null : Number(e.target.value) } : x) } : d)} />
              <input type="number" className={cn(inputCls, "col-span-3")} placeholder="Ualá" value={h.uala ?? ""} onChange={(e) => setDraft((d) => d ? { ...d, history: d.history.map((x) => x.id === h.id ? { ...x, uala: e.target.value === "" ? null : Number(e.target.value) } : x) } : d)} />
              <button type="button" aria-label={`Eliminar historial ${h.month_label || h.id}`} onClick={() => {
                if (!confirmRemove(h.month_label || "mes de historial")) return;
                setDraft((d) => d ? { ...d, history: d.history.filter((x) => x.id !== h.id) } : d);
                if (h.id > 0) setRemoved((r) => ({ ...r, history: [...r.history, h.id] }));
              }} className="col-span-1 flex justify-center text-red-400/70 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Campo con etiqueta (formularios Oruon) ───────────────────────────────────────
function Field({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <label className={cn("block", className)}>
      <span className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-white/40">{label}</span>
      {children}
    </label>
  );
}

// ── VIEW: Transacciones (libro unificado Oruon) ───────────────────────────────────
interface TxForm {
  id: number; tx_date: string; description: string; kind: TxKind; amount: string;
  currency: FinanzasCurrency; category: string; account: string; note: string;
  origin: FinanzasTransaction["origin"]; recurring_id: number | null;
}
const emptyTxForm = (kind: TxKind = "expense"): TxForm => ({
  id: nextNegId(), tx_date: todayISO(), description: "", kind, amount: "", currency: "ARS",
  category: "", account: "", note: "", origin: "manual", recurring_id: null,
});
const txToForm = (t: FinanzasTransaction): TxForm => ({
  id: t.id, tx_date: t.tx_date, description: t.description, kind: t.kind, amount: String(t.amount ?? ""),
  currency: t.currency, category: t.category ?? "", account: t.account ?? "", note: t.note ?? "",
  origin: t.origin, recurring_id: t.recurring_id,
});

function TxFormCard({ form, setForm, onSave, onCancel, saving }: {
  form: TxForm; setForm: (f: TxForm) => void; onSave: (f: TxForm) => void; onCancel: () => void; saving: boolean;
}) {
  const upd = (patch: Partial<TxForm>) => setForm({ ...form, ...patch });
  return (
    <Card className="border-violet-500/25 bg-violet-500/[0.04]">
      <CardHeader className="pb-2"><CardTitle className="text-sm text-white/85">{form.id < 0 ? "Nueva transacción" : "Editar transacción"}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          {(["expense", "income"] as TxKind[]).map((k) => (
            <button key={k} type="button" onClick={() => upd({ kind: k })}
              className={cn("flex-1 rounded-lg border px-3 py-2 text-[13px] font-semibold transition",
                form.kind === k ? (k === "income" ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-200" : "border-red-500/40 bg-red-500/15 text-red-200") : "border-white/10 text-white/45 hover:text-white/70")}>
              {k === "income" ? "＋ Ingreso" : "－ Gasto"}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Field label="Fecha"><input type="date" value={form.tx_date} onChange={(e) => upd({ tx_date: e.target.value })} className={inputCls} /></Field>
          <Field label="Monto"><input type="number" inputMode="decimal" value={form.amount} onChange={(e) => upd({ amount: e.target.value })} placeholder="0" className={inputCls} /></Field>
          <Field label="Moneda"><select value={form.currency} onChange={(e) => upd({ currency: e.target.value as FinanzasCurrency })} className={inputCls}><option value="ARS">ARS</option><option value="USD">USD</option></select></Field>
          <Field label="Cuenta"><input value={form.account} onChange={(e) => upd({ account: e.target.value })} placeholder="Opcional" className={inputCls} /></Field>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Descripción"><input value={form.description} onChange={(e) => upd({ description: e.target.value })} placeholder="Ej. café" className={inputCls} /></Field>
          <Field label="Categoría">
            <input list="tx-cats" value={form.category} onChange={(e) => upd({ category: e.target.value })} placeholder="Ej. Comida" className={inputCls} />
            <datalist id="tx-cats">{TX_CATEGORY_META.map((c) => <option key={c.key} value={c.key} />)}</datalist>
          </Field>
        </div>
        <Field label="Nota"><input value={form.note} onChange={(e) => upd({ note: e.target.value })} placeholder="Opcional" className={inputCls} /></Field>
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving}><X className="mr-1 h-3.5 w-3.5" />Cancelar</Button>
          <Button size="sm" onClick={() => onSave(form)} disabled={saving}>{saving ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />}Guardar</Button>
        </div>
      </CardContent>
    </Card>
  );
}

// Fila unificada del libro: manual | diario API | real MercadoPago (sin Stripe).
// Contabilidad: ingreso real = BRUTO; comisión MP = gasto separado → balance = neto.
interface LedgerRow {
  key: string;
  date: string;
  /** true si no hay due_date/timestamps → solo visible en “Ver todas”. */
  undated?: boolean;
  description: string;
  kind: TxKind;
  amount: number; // bruto en ingresos reales; fee en filas de comisión
  net: number | null; // neto real (si se conoce) — subtítulo en income
  currency: FinanzasCurrency;
  category: string | null;
  account: string | null;
  origin: string; // manual|recurring|import|api|mercadopago
  note: string | null;
  real: boolean; // read-only (dato automático MP)
  manual?: FinanzasTransaction; // para editar/borrar libro manual
  /** Item de plan/diario API (finanzas_items) — borrar vía table items. */
  item?: FinanzasItem;
  /** Movimiento de CUENTA MP (extracto): transferencias/ingresos no afectan balance. */
  accountTx?: FinanzasAccountTx;
  /** Ingreso real MP con detalle enriquecido. */
  realTx?: FinanzasRealTx;
  /** Email / nombre / tipo MP visibles en UI. */
  customer_email?: string | null;
  customer_name?: string | null;
  transaction_type?: string | null;
  operation_type?: string | null;
  payment_method_id?: string | null;
  payment_type_id?: string | null;
  transaction_number?: string | null;
  cashflow?: string | null;
  summary_es?: string | null;
  external_reference?: string | null;
  business_sub_unit?: string | null;
  poi_type?: string | null;
  /** Desembolso MP Short Loan / Mercado Crédito (no es venta). */
  is_loan?: boolean;
  loan_kind?: string | null;
  merchant_key?: string | null;
  poi_sub_type?: string | null;
  poi_branch?: string | null;
  wallet_balance_before?: number | null;
  wallet_balance_after?: number | null;
  funding_trace?: WalletOutflowTrace | null;
  money_in?: MoneyInDetail | null;
  /** payer_mp = no es destino bancario garantizado */
  email_role?: "payer_mp" | "buyer" | "unknown" | null;
}
const SOURCE_BADGE: Record<string, { emoji: string; label: string }> = {
  mercadopago: { emoji: "🔵", label: "MercadoPago" },
  recurring: { emoji: "🔁", label: "Recurrente" },
  import: { emoji: "📥", label: "Diario" },
  /** Diario vía API externa — no mostrar nombre de integrador en UI. */
  api: { emoji: "🔗", label: "API" },
  lara: { emoji: "🔗", label: "API" }, // legacy origin key, display as API
  manual: { emoji: "✍️", label: "Manual" },
};
const manualToLedger = (t: FinanzasTransaction): LedgerRow => ({
  key: `m${t.id}`, date: t.tx_date, description: t.description, kind: t.kind, amount: t.amount, net: null,
  currency: t.currency, category: t.category, account: t.account, origin: t.origin, note: t.note, real: false, manual: t,
});

/**
 * Items del diario (API externa / plan) → libro unificado.
 * - ingreso → income (afecta balance)
 * - gasto → expense
 * - transferencia / ahorro → se listan pero NO distorsionan balance
 * Fecha: due_date → updated_at → created_at. Sin inventar “hoy”.
 */
const itemToLedger = (it: FinanzasItem): LedgerRow => {
  const meta = (it.metadata ?? {}) as Record<string, unknown>;
  const fromWa = String(meta.source ?? "") === "whatsapp";
  const curRaw = String(it.currency ?? "ARS").toUpperCase();
  const currency: FinanzasCurrency = curRaw === "USD" ? "USD" : "ARS";
  const kind: TxKind = it.section === "ingreso" ? "income" : "expense";
  const neutral = it.section === "ahorro" || it.section === "transferencia";
  const resolved = itemLedgerDate(it);
  const undated = !resolved;
  const date = resolved ?? "";
  const suffix =
    it.section === "ahorro" ? " · ahorro" : it.section === "transferencia" ? " · transferencia" : "";
  return {
    key: `item:${it.id}`,
    date,
    undated,
    description: `${it.label}${suffix}`,
    kind,
    amount: it.amount ?? 0,
    net: null,
    currency,
    category: it.category ?? (neutral ? it.section : null),
    // API externa: no mostrar marca de terceros; solo canal genérico.
    account: fromWa ? "WhatsApp · API" : "Diario · API",
    origin: "api",
    note: [
      undated ? "Sin fecha (solo en “Ver todas”)" : null,
      neutral ? "No suma al balance del libro (transferencia/ahorro)" : null,
      it.note,
    ]
      .filter(Boolean)
      .join(" · ") || null,
    real: false,
    item: it,
  };
};

/**
 * ¿La fila afecta balance del libro?
 * - Excluye transferencias/ahorros del diario y transferencias/fondeos de cuenta.
 * - Préstamos (desembolso) SÍ se listan como ingreso de dinero prestado, pero NO
 *   se mezclan con “ganancia”: se excluyen del balance (es deuda, no venta).
 *   Misma filosofía manager-agent: is_loan separado de ventas / link de pago.
 */
function ledgerAffectsBalance(
  r: Pick<LedgerRow, "item" | "origin" | "category" | "accountTx" | "is_loan">,
): boolean {
  if (r.is_loan) return false;
  if (r.item && (r.item.section === "ahorro" || r.item.section === "transferencia")) return false;
  // Extracto MP: transferencias e ingresos de dinero mueven plata entre cuentas
  // propias / no son ventas — se listan pero no distorsionan ingresos/gastos.
  if (
    r.accountTx &&
    (r.accountTx.activity === "transferencia" ||
      r.accountTx.activity === "ingreso_dinero" ||
      r.accountTx.activity === "prestamo")
  ) {
    return false;
  }
  return true;
}

const ACCOUNT_ACTIVITY_META: Record<
  FinanzasAccountTx["activity"],
  { label: string; category: string }
> = {
  transferencia: { label: "Transferencia", category: "Transferencias" },
  ingreso_dinero: { label: "Ingreso de dinero", category: "Ingresos de dinero" },
  compra: { label: "Compra", category: "Consumo" },
  suscripcion_servicio: { label: "Suscripción", category: "Suscripciones" },
  prestamo: { label: "Préstamo · desembolso", category: "Préstamos" },
  otro: { label: "Movimiento", category: "Otros" },
};

const LOAN_KIND_LABEL: Record<string, string> = {
  short_loan_outlay: "Préstamo corto · desembolso",
  short_loan: "Préstamo corto",
  consumer_credit: "Mercado Crédito",
  loan: "Préstamo Mercado Pago",
};

/** Etiquetas legibles para payment_method_id / payment_type_id de MP. */
function humanMpMethod(id: string | null | undefined): string | null {
  if (!id) return null;
  const k = id.toLowerCase();
  const map: Record<string, string> = {
    account_money: "Dinero en cuenta",
    bank_transfer: "Transferencia bancaria",
    debit_card: "Débito",
    credit_card: "Crédito",
    prepaid_card: "Prepaga",
    ticket: "Efectivo (Rapipago/PagoFácil)",
    atm: "Cajero",
    digital_currency: "Cripto",
    visa: "Visa",
    master: "Mastercard",
    debvisa: "Visa débito",
    debmaster: "Mastercard débito",
    amex: "Amex",
    naranja: "Naranja",
    cabal: "Cabal",
  };
  return map[k] ?? id;
}

/** Movimiento de cuenta MP → libro. Compras/suscripciones = gasto real; resto neutro. */
const accountToLedger = (r: FinanzasAccountTx): LedgerRow => {
  const meta = ACCOUNT_ACTIVITY_META[r.activity];
  const typeLabel = r.transaction_type || meta.label;
  const statusNote =
    r.status === "pending" ? "pendiente" : r.status === "refunded" ? "reembolsado" : null;
  return {
    key: r.id,
    date: r.tx_date,
    description: r.description,
    kind: r.direction === "in" ? "income" : "expense",
    amount: r.amount,
    net: r.net_amount ?? null,
    currency: r.currency,
    category: meta.category,
    account: "MercadoPago",
    origin: "mercadopago",
    note: statusNote,
    real: true,
    accountTx: r,
    customer_email: r.customer_email ?? null,
    customer_name: r.customer_name ?? null,
    transaction_type: typeLabel,
    operation_type: r.operation_type,
    payment_method_id: r.payment_method_id ?? null,
    payment_type_id: r.payment_type_id ?? null,
    transaction_number: r.transaction_number ?? null,
    cashflow: r.cashflow ?? r.direction,
    summary_es: r.summary_es ?? null,
    external_reference: r.external_reference ?? null,
    business_sub_unit: r.business_sub_unit ?? null,
    poi_type: r.poi_type ?? null,
    is_loan: Boolean(r.is_loan) || r.activity === "prestamo",
    loan_kind: r.loan_kind ?? null,
    merchant_key: r.merchant_key ?? null,
    poi_sub_type: r.poi_sub_type ?? null,
    poi_branch: r.poi_branch ?? null,
    wallet_balance_before: r.wallet_balance_before ?? null,
    wallet_balance_after: r.wallet_balance_after ?? null,
    funding_trace: r.funding_trace ?? null,
    money_in: r.money_in ?? null,
    email_role: r.email_role ?? (r.customer_email ? "payer_mp" : null),
  };
};
/** Ingreso real MP → libro. Monto = BRUTO; neto en subtítulo. Fee MP va en fila de gasto. */
const realToLedger = (r: FinanzasRealTx): LedgerRow => ({
  key: r.id,
  date: r.tx_date,
  description: r.description,
  kind: "income",
  amount: r.gross,
  net: r.net,
  currency: r.currency,
  category: r.is_loan ? "Préstamos" : r.is_subscription ? "Suscripción" : "Pago",
  account: "MercadoPago",
  origin: "mercadopago",
  note: null,
  real: true,
  realTx: r,
  customer_email: r.customer_email,
  customer_name: r.customer_name ?? null,
  transaction_type: r.is_loan
    ? (r.loan_kind ? LOAN_KIND_LABEL[r.loan_kind] ?? "Préstamo" : "Préstamo")
    : r.transaction_type ?? (r.is_subscription ? "Suscripción" : "Pago"),
  operation_type: r.operation_type ?? null,
  payment_method_id: r.payment_method_id ?? null,
  payment_type_id: r.payment_type_id ?? null,
  transaction_number: r.transaction_number ?? null,
  cashflow: r.is_loan ? "in" : r.cashflow ?? "in",
  summary_es: r.summary_es ?? r.money_in?.narrative_es ?? null,
  external_reference: r.external_reference ?? null,
  is_loan: Boolean(r.is_loan),
  loan_kind: r.loan_kind ?? null,
  merchant_key: r.merchant_key ?? null,
  poi_sub_type: r.poi_sub_type ?? null,
  poi_branch: r.poi_branch ?? null,
  wallet_balance_before: r.wallet_balance_before ?? null,
  wallet_balance_after: r.wallet_balance_after ?? null,
  funding_trace: r.funding_trace ?? null,
  money_in: r.money_in ?? null,
  email_role: r.customer_email ? (r.is_loan ? "payer_mp" : "buyer") : null,
});

/** Comisión MP como gasto real (bruto − neto). Solo filas con fee > 0. No aplica a préstamos. */
const mpCommissionToLedger = (r: FinanzasRealTx): LedgerRow | null => {
  if (r.source !== "mercadopago" || r.net == null || r.is_loan) return null;
  const fee = Math.max(0, r.gross - r.net);
  if (fee < 0.01) return null;
  return {
    key: `${r.id}:fee`,
    date: r.tx_date,
    description: `Comisión MercadoPago — ${r.description}`,
    kind: "expense",
    amount: fee,
    net: null,
    currency: r.currency,
    category: "Comisiones",
    account: "MercadoPago",
    origin: "mercadopago",
    note: null,
    real: true,
    customer_email: r.customer_email,
    transaction_type: "Comisión MP",
    transaction_number: r.transaction_number ?? null,
  };
};

const FUNDING_KIND_LABEL: Record<string, string> = {
  sale: "Cobro",
  transfer_in: "Transferencia entrante",
  loan: "Préstamo",
  other_in: "Ingreso",
};

const COUNTERPARTY_ROLE_LABEL: Record<string, string> = {
  buyer: "Comprador (pagó el cobro)",
  payer_mp: "Payer MP de un tercero (posible origen)",
  account_self: "Payer = cuenta propia — NO es el origen del dinero",
  mercadopago_credit: "Mercado Crédito / desembolso",
  origin_unknown: "Origen no identificado en el Payment",
  unknown: "Desconocido",
};

/** Bloque denso de detalle cuando entra plata a la wallet. */
function MoneyInDetailBlock({ m }: { m: MoneyInDetail }) {
  const rows: Array<{ label: string; value: string; mono?: boolean; accent?: boolean }> = [];
  rows.push({ label: "Tipo de ingreso", value: m.kind_label, accent: true });
  rows.push({
    label: "Contraparte",
    value: COUNTERPARTY_ROLE_LABEL[m.counterparty_role] ?? m.counterparty_role,
  });
  if (m.counterparty_email) {
    rows.push({
      label:
        m.counterparty_role === "buyer"
          ? "Email comprador"
          : m.counterparty_role === "account_self"
            ? "Email en API (cuenta propia)"
            : "Email payer",
      value: m.counterparty_email,
      accent: m.counterparty_role === "buyer" || m.counterparty_role === "payer_mp",
    });
  }
  if (m.email_is_not_origin || m.counterparty_role === "account_self") {
    rows.push({
      label: "Origen del dinero",
      value: "No está en payer.email — puede ser CVU/banco/tercero no expuesto por MP en este campo",
      accent: true,
    });
  }
  if (m.counterparty_name) rows.push({ label: "Nombre", value: m.counterparty_name });
  rows.push({ label: "Bruto", value: fmtMoney(m.gross, m.currency, 2) });
  if (m.net != null) rows.push({ label: "Neto acreditado", value: fmtMoney(m.net, m.currency, 2), accent: true });
  if (m.fee != null && m.fee > 0) rows.push({ label: "Fee / comisión", value: fmtMoney(m.fee, m.currency, 2) });
  if (m.total_paid != null && Math.abs(m.total_paid - m.gross) > 0.01) {
    rows.push({ label: "Total pagado", value: fmtMoney(m.total_paid, m.currency, 2) });
  }
  if (m.pack_id) rows.push({ label: "Pack / producto", value: m.pack_id });
  if (m.installments != null) rows.push({ label: "Cuotas", value: String(m.installments) });
  if (m.operation_type) rows.push({ label: "MP operation", value: m.operation_type, mono: true });
  if (m.payment_method_id) {
    rows.push({ label: "Método", value: humanMpMethod(m.payment_method_id) ?? m.payment_method_id });
  }
  if (m.payment_type_id) {
    rows.push({ label: "Medio", value: humanMpMethod(m.payment_type_id) ?? m.payment_type_id });
  }
  if (m.business_unit) rows.push({ label: "Business unit", value: m.business_unit, mono: true });
  if (m.business_sub_unit) rows.push({ label: "Flujo", value: m.business_sub_unit, mono: true });
  if (m.poi_type) rows.push({ label: "POI", value: m.poi_type, mono: true });
  if (m.poi_sub_type) rows.push({ label: "POI sub", value: m.poi_sub_type, mono: true });
  if (m.poi_branch) rows.push({ label: "Branch", value: m.poi_branch, mono: true });
  if (m.external_reference) rows.push({ label: "Ref externa", value: m.external_reference, mono: true });
  if (m.mp_status) rows.push({ label: "Estado MP", value: m.mp_status });
  if (m.mp_status_detail) rows.push({ label: "Detalle estado", value: m.mp_status_detail, mono: true });

  return (
    <div className="rounded-lg border border-emerald-500/25 bg-emerald-500/[0.06] px-2.5 py-2 text-[11px]">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-200/80">
          Detalle del ingreso
        </span>
        <span className="rounded-full bg-emerald-500/20 px-1.5 py-px text-[9px] font-bold uppercase text-emerald-100">
          entra plata
        </span>
        <span className="rounded-full border border-emerald-400/20 bg-white/[0.04] px-1.5 py-px text-[9px] font-medium text-emerald-100/90">
          {m.kind_label}
        </span>
      </div>
      <p className="mt-1 break-words text-[11px] leading-snug text-emerald-50/90">{m.narrative_es}</p>
      <dl className="mt-1.5 grid grid-cols-1 gap-x-3 gap-y-0.5 sm:grid-cols-2">
        {rows.map((row) => (
          <div key={row.label} className="flex min-w-0 gap-1.5">
            <dt className="shrink-0 text-white/40">{row.label}</dt>
            <dd
              className={cn(
                "min-w-0 break-all",
                row.accent ? "font-medium text-emerald-100" : "text-white/70",
                row.mono && "font-mono text-[10px] text-white/55",
              )}
            >
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
      {m.kind === "loan" && (
        <p className="mt-1.5 text-[10px] text-amber-100/75">
          Esto es deuda acreditada (desembolso), no un cobro de cliente. No suma como “ganancia” en el
          balance del libro.
        </p>
      )}
      {(m.kind === "transfer_in" || m.kind === "account_fund") && (
        <p className="mt-1.5 text-[10px] text-amber-100/70">
          {m.counterparty_role === "account_self" ? (
            <>
              En fondeos/CVU, MP suele poner en <code className="text-white/50">payer.email</code> el
              email de <strong>esta misma cuenta</strong>. Eso <strong>no significa</strong> que esa
              persona “mandó” el dinero: el origen real (CBU/alias/banco) no viene en ese campo.
            </>
          ) : (
            <>
              Transferencia entrante: el email es el payer de la API de MP, no necesariamente el
              CBU/alias de origen bancario.
            </>
          )}
        </p>
      )}
    </div>
  );
}

/** De dónde vino la plata antes de un envío grande (fuentes + totales). */
function FundingTraceBlock({ t }: { t: WalletOutflowTrace }) {
  const sources = t.sources ?? [];
  const windowIn = t.prior_inflows_window ?? t.prior_inflows_month;
  const moneyIn = t.money_in_before ?? t.prior_inflows_window_sum ?? t.prior_inflows_month_sum;
  const moneyOut = t.money_out_before ?? 0;

  return (
    <div
      className={cn(
        "rounded-lg border px-2.5 py-2 text-[11px]",
        t.covered_by_balance
          ? "border-emerald-500/25 bg-emerald-500/[0.06]"
          : "border-amber-500/30 bg-amber-500/[0.07]",
      )}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <span className="font-semibold uppercase tracking-wide text-[10px] text-white/50">
          ¿De dónde vino la plata?
        </span>
        {t.covered_by_balance ? (
          <span className="rounded-full bg-emerald-500/20 px-1.5 py-px text-[9px] font-bold uppercase text-emerald-200">
            saldo del extracto cubre el envío
          </span>
        ) : (
          <span className="rounded-full bg-amber-500/20 px-1.5 py-px text-[9px] font-bold uppercase text-amber-100">
            extracto incompleto · MP aprobó con más saldo real
          </span>
        )}
      </div>

      {t.narrative_es && (
        <p className="mt-1.5 text-[11px] leading-snug text-white/80">{t.narrative_es}</p>
      )}

      <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <div className="rounded-md bg-black/20 px-2 py-1.5">
          <div className="text-[9px] uppercase text-white/35">Envió</div>
          <div className="tabular-nums font-bold text-rose-300">−{fmtMoney(t.amount, "ARS")}</div>
        </div>
        <div className="rounded-md bg-black/20 px-2 py-1.5">
          <div className="text-[9px] uppercase text-white/35">Entró antes (ventana)</div>
          <div className="tabular-nums font-bold text-emerald-300">
            +{fmtMoney(moneyIn, "ARS")}
          </div>
          <div className="text-[9px] text-white/35">{t.n_in_before ?? windowIn.length} mov.</div>
        </div>
        <div className="rounded-md bg-black/20 px-2 py-1.5">
          <div className="text-[9px] uppercase text-white/35">Ya había salido</div>
          <div className="tabular-nums font-semibold text-rose-200/90">
            −{fmtMoney(moneyOut, "ARS")}
          </div>
          <div className="text-[9px] text-white/35">{t.n_out_before ?? "—"} mov.</div>
        </div>
        <div className="rounded-md bg-black/20 px-2 py-1.5">
          <div className="text-[9px] uppercase text-white/35">Saldo extracto antes</div>
          <div
            className={cn(
              "tabular-nums font-bold",
              t.balance_before >= 0 ? "text-white/90" : "text-amber-200",
            )}
          >
            {fmtMoney(t.balance_before, "ARS")}
          </div>
          <div className="text-[9px] text-white/35">= entró − salió (base 0)</div>
        </div>
      </div>

      {sources.length > 0 && (
        <div className="mt-2">
          <div className="text-[10px] font-medium text-white/45">
            Orígenes del dinero que entró antes de este envío
          </div>
          <ul className="mt-1 space-y-1">
            {sources.slice(0, 8).map((s) => (
              <li
                key={s.key}
                className="flex min-w-0 items-baseline justify-between gap-2 rounded-md border border-white/[0.06] bg-white/[0.03] px-2 py-1.5"
              >
                <div className="min-w-0">
                  <div className="break-words font-medium text-white/85">{s.label}</div>
                  <div className="text-[9px] text-white/40">
                    {s.n} ingreso{s.n === 1 ? "" : "s"}
                    {s.email ? ` · contraparte ${s.email}` : " · sin atribuir a un email de origen"}
                    {" · "}
                    {FUNDING_KIND_LABEL[s.kind] ?? s.kind}
                  </div>
                </div>
                <div className="shrink-0 text-right tabular-nums font-bold text-emerald-300">
                  +{fmtMoney(s.sum, "ARS")}
                  <div className="text-[9px] font-normal text-white/35">
                    {moneyIn > 0 ? `${Math.round((s.sum / moneyIn) * 100)}% del volumen en extracto` : ""}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {windowIn.length > 0 && (
        <div className="mt-2">
          <div className="text-[10px] font-medium text-white/40">
            Detalle de ingresos previos (más recientes primero)
          </div>
          <ul className="mt-1 max-h-48 space-y-0.5 overflow-y-auto">
            {windowIn.slice(0, 15).map((inf) => (
              <li
                key={inf.id}
                className="flex min-w-0 flex-col gap-0.5 border-b border-white/[0.04] py-1 last:border-0 text-[10px] text-white/60"
              >
                <div className="flex min-w-0 flex-wrap items-baseline justify-between gap-x-2">
                  <span className="min-w-0 break-words">
                    <span className="text-white/40">{fmtShortDate(inf.tx_date)}</span>
                    {" · "}
                    <span className="text-sky-200/70">{FUNDING_KIND_LABEL[inf.kind] ?? inf.kind}</span>
                    {" · "}
                    {inf.description}
                  </span>
                  <span className="shrink-0 tabular-nums font-semibold text-emerald-300/90">
                    +{fmtMoney(inf.amount, "ARS")}
                  </span>
                </div>
                {(inf.email || inf.money_in?.pack_id || inf.money_in?.payment_method_id) && (
                  <div className="text-[9px] text-white/40">
                    {inf.email ? (
                      <span className="text-amber-100/80">
                        {inf.kind === "sale" ? "comprador" : "payer"} {inf.email}
                      </span>
                    ) : null}
                    {inf.money_in?.pack_id ? ` · pack ${inf.money_in.pack_id}` : ""}
                    {inf.money_in?.payment_method_id
                      ? ` · ${humanMpMethod(inf.money_in.payment_method_id) ?? inf.money_in.payment_method_id}`
                      : ""}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="mt-2 text-[10px] leading-snug text-white/40">
        En el mes {t.month} entraron {fmtMoney(t.prior_inflows_month_sum, "ARS")} antes de este envío.
        {t.covered_by_balance
          ? " El extracto alcanza para este movimiento."
          : " El extracto (base $0 al inicio de la ventana) no alcanza; MP usó saldo real fuera de este recorte."}
      </p>
    </div>
  );
}

/** Tabla mes a mes: capacidad wallet (ingresos vs salidas / transferencias). */
function WalletMonthCapacityPanel({
  byMonth,
  largeOutflows,
  lookbackDays,
  note,
  closing,
  compact = false,
}: {
  byMonth: WalletMonthRollup[];
  largeOutflows: WalletOutflowTrace[];
  lookbackDays: number;
  note: string;
  closing: number;
  /** Si true, menos padding (para embeber). */
  compact?: boolean;
}) {
  if (!byMonth.length) return null;
  return (
    <div className={cn("space-y-3", compact && "space-y-2")}>
      <div className="rounded-xl border border-sky-500/20 bg-sky-500/[0.06] px-3 py-2.5">
        <div className="flex flex-wrap items-baseline justify-between gap-2 text-[11px]">
          <span className="font-semibold text-sky-100">
            Capacidad wallet MP · mes a mes ({lookbackDays}d)
          </span>
          <span className="tabular-nums text-white/70">
            cierre extracto{" "}
            <strong className="text-base text-white">{fmtMoney(closing, "ARS")}</strong>
          </span>
        </div>
        <p className="mt-1 text-[10px] leading-snug text-white/45">{note}</p>
      </div>

      <div className="overflow-x-hidden rounded-xl border border-white/10 bg-white/[0.02]">
        <div className="border-b border-white/10 px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-white/40">
          Extracto por mes · apertura → ingresos → salidas → cierre
        </div>
        <div className="divide-y divide-white/[0.06]">
          {byMonth.map((m) => (
            <div key={m.month} className="min-w-0 px-3 py-2.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-semibold text-white/90">{m.label}</span>
                  {m.solvent ? (
                    <span className="rounded-full bg-emerald-500/15 px-1.5 py-px text-[9px] font-bold uppercase text-emerald-300">
                      cierre ≥ 0
                    </span>
                  ) : (
                    <span className="rounded-full bg-rose-500/15 px-1.5 py-px text-[9px] font-bold uppercase text-rose-300">
                      cierre negativo
                    </span>
                  )}
                </div>
                <span className="tabular-nums text-[12px] font-bold text-white/85">
                  {fmtMoney(m.closing, "ARS")}
                </span>
              </div>
              <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] sm:grid-cols-4">
                <div>
                  <div className="text-[10px] text-white/35">Apertura</div>
                  <div className="tabular-nums text-white/70">{fmtMoney(m.opening, "ARS")}</div>
                </div>
                <div>
                  <div className="text-[10px] text-white/35">Ingresos ({m.n_in})</div>
                  <div className="tabular-nums font-semibold text-emerald-300">
                    +{fmtMoney(m.in_total, "ARS")}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-white/35">Salidas ({m.n_out})</div>
                  <div className="tabular-nums font-semibold text-rose-300">
                    −{fmtMoney(m.out_total, "ARS")}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-white/35">
                    Transferencias out ({m.n_transfer_out})
                  </div>
                  <div className="tabular-nums text-amber-200/90">
                    −{fmtMoney(m.transfer_out, "ARS")}
                  </div>
                </div>
              </div>
              {/* Capacidad visual: ingresos del mes vs transferencias salientes */}
              {m.transfer_out > 0 && (
                <div className="mt-2 rounded-md border border-white/8 bg-black/20 px-2 py-1.5 text-[10px] text-white/50">
                  {m.in_total + m.opening + 0.01 >= m.transfer_out ? (
                    <span className="text-emerald-200/85">
                      Capacidad del mes: apertura + ingresos (
                      {fmtMoney(m.opening + m.in_total, "ARS")}) cubren las transferencias salientes (
                      {fmtMoney(m.transfer_out, "ARS")}).
                    </span>
                  ) : (
                    <span className="text-rose-200/85">
                      En el extracto, apertura + ingresos del mes (
                      {fmtMoney(m.opening + m.in_total, "ARS")}) no alcanzan las transferencias out (
                      {fmtMoney(m.transfer_out, "ARS")}). Falta historial previo a la ventana o el
                      saldo real de MP era mayor.
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {largeOutflows.length > 0 && (
        <div className="rounded-xl border border-amber-500/25 bg-amber-500/[0.05]">
          <div className="border-b border-amber-500/20 px-3 py-2 text-[11px] font-medium text-amber-100/90">
            Trazabilidad de salidas grandes / transferencias
          </div>
          <div className="divide-y divide-white/[0.05]">
            {largeOutflows.slice(0, 20).map((o) => (
              <div key={o.id} className="px-3 py-2.5">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[12px] font-semibold text-white/90">
                      {fmtShortDate(o.tx_date)} · {o.description}
                    </div>
                    <div className="mt-0.5 text-[10px] text-white/40 font-mono">{o.id}</div>
                  </div>
                  <div className="text-right">
                    <div className="tabular-nums text-[13px] font-bold text-rose-300">
                      −{fmtMoney(o.amount, "ARS")}
                    </div>
                    {o.covered_by_balance ? (
                      <span className="text-[9px] font-bold uppercase text-emerald-300">cubierto</span>
                    ) : (
                      <span className="text-[9px] font-bold uppercase text-rose-300">
                        extracto corto
                      </span>
                    )}
                  </div>
                </div>
                <div className="mt-1.5">
                  <FundingTraceBlock t={o} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Tab dedicado: trazabilidad de capacidad y orígenes del dinero (solo MP).
 * Separado de Transacciones para leer mes a mes + “¿de dónde vino la plata?”
 * sin mezclar con el listado operativo.
 */
export function TrazabilidadTab({
  data,
  isFetching,
  onRefresh,
}: {
  data: FinanzasData;
  isFetching?: boolean;
  onRefresh?: () => void;
}) {
  const wb = data.real?.walletBalance;
  const byMonth = wb?.byMonth ?? [];
  const largeOutflows = wb?.largeOutflows ?? [];
  const income = data.real?.incomeTransactions ?? EMPTY_REAL_TX;
  const account = data.real?.accountActivity ?? EMPTY_ACCOUNT_TX;

  // Resumen de ingresos del feed con money_in (para tabla “qué entró”)
  const moneyInRows = useMemo(() => {
    type Row = {
      id: string;
      tx_date: string;
      description: string;
      amount: number;
      kind: string;
      email: string | null;
      role: string | null;
      money_in: MoneyInDetail | null;
    };
    const rows: Row[] = [];
    for (const t of income) {
      if (t.currency !== "ARS") continue;
      const amt = t.net != null && Number.isFinite(t.net) ? t.net : t.gross;
      rows.push({
        id: t.id,
        tx_date: t.tx_date,
        description: t.description,
        amount: amt,
        kind: t.is_loan ? "loan" : t.is_subscription ? "subscription" : "sale",
        email: t.customer_email,
        role: t.money_in?.counterparty_role ?? (t.is_loan ? "mercadopago_credit" : "buyer"),
        money_in: t.money_in ?? null,
      });
    }
    for (const t of account) {
      if (t.currency !== "ARS" || t.status !== "completed") continue;
      const cf = t.cashflow ?? t.direction;
      if (cf !== "in" && !t.is_loan) continue;
      rows.push({
        id: t.id,
        tx_date: t.tx_date,
        description: t.description,
        amount: Math.abs(t.amount),
        kind: t.is_loan
          ? "loan"
          : t.activity === "ingreso_dinero"
            ? "account_fund"
            : t.activity === "transferencia"
              ? "transfer_in"
              : "other_in",
        email: t.customer_email ?? null,
        role: t.money_in?.counterparty_role ?? t.email_role ?? null,
        money_in: t.money_in ?? null,
      });
    }
    return rows.sort((a, b) => (a.tx_date < b.tx_date ? 1 : a.tx_date > b.tx_date ? -1 : 0));
  }, [income, account]);

  const moneyInTotal = useMemo(
    () => moneyInRows.reduce((s, r) => s + r.amount, 0),
    [moneyInRows],
  );

  if (!data.real) {
    return (
      <div className="rounded-xl border border-dashed border-white/15 px-4 py-12 text-center text-[13px] text-white/40">
        Feed MercadoPago no disponible. Usá <strong className="text-white/60">Actualizar DB</strong>{" "}
        en Transacciones o el botón del header.
      </div>
    );
  }

  return (
    <div className="min-w-0 space-y-4 overflow-x-hidden">
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-3 rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-500/[0.08] to-transparent px-3 py-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2 text-[12px] font-semibold text-amber-100/90">
            <span className="text-base" aria-hidden>
              🔗
            </span>
            Trazabilidad wallet · solo MercadoPago
          </div>
          <p className="max-w-2xl text-[11px] leading-relaxed text-white/50">
            Entendé <strong className="text-white/70">mes a mes</strong> cuánto entró y salió, y en
            cada transferencia grande <strong className="text-white/70">de dónde vino la plata</strong>{" "}
            (orígenes honestos: cobros con comprador; fondeos/CVU sin inventar origen en payer.email).
            No incluye altas manuales ni Stripe.
          </p>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-white/45">
            <span>
              Ingresos en feed:{" "}
              <span className="font-semibold tabular-nums text-emerald-300">
                {fmtMoney(moneyInTotal, "ARS")}
              </span>
              <span className="text-white/30"> · {moneyInRows.length} mov.</span>
            </span>
            <span className="text-white/25">·</span>
            <span>
              Salidas grandes:{" "}
              <span className="font-semibold tabular-nums text-rose-300">{largeOutflows.length}</span>
            </span>
            {wb?.lookbackDays != null && (
              <>
                <span className="text-white/25">·</span>
                <span>ventana {wb.lookbackDays}d</span>
              </>
            )}
          </div>
        </div>
        {onRefresh && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={isFetching}
            onClick={onRefresh}
            className="h-8 border-amber-500/30 text-[12px] text-amber-100"
            title="Releer extracto MP desde Postgres"
          >
            <RefreshCw className={cn("mr-1.5 h-3.5 w-3.5", isFetching && "animate-spin")} />
            {isFetching ? "Leyendo…" : "Actualizar DB"}
          </Button>
        )}
      </div>

      {wb && byMonth.length > 0 ? (
        <WalletMonthCapacityPanel
          byMonth={byMonth}
          largeOutflows={largeOutflows}
          lookbackDays={wb.lookbackDays}
          note={wb.note}
          closing={wb.closing}
        />
      ) : (
        <div className="rounded-xl border border-dashed border-white/10 px-4 py-8 text-center text-[12px] text-white/40">
          Sin rollup de saldo aún. Actualizá el feed o ampliá la ventana de días del extracto.
        </div>
      )}

      {/* Listado compacto de lo que entró (para cruzar con salidas) */}
      <Card className="min-w-0 overflow-hidden border-white/10 bg-white/[0.02]">
        <div className="border-b border-white/10 px-3 py-2.5 sm:px-4">
          <div className="text-[12px] font-semibold text-white/85">Qué entró a la cuenta (feed MP)</div>
          <p className="mt-0.5 text-[10px] text-white/40">
            Cobros de marca, fondeos/CVU, transferencias entrantes y préstamos. El email en fondeos de
            la cuenta propia <strong className="text-white/55">no es el origen del dinero</strong>.
          </p>
        </div>
        {moneyInRows.length === 0 ? (
          <div className="px-4 py-10 text-center text-[12px] text-white/35">Sin ingresos en el feed.</div>
        ) : (
          <div className="max-h-[28rem] divide-y divide-white/[0.05] overflow-y-auto">
            {moneyInRows.slice(0, 80).map((r) => (
              <div key={r.id} className="min-w-0 px-3 py-2.5 sm:px-4">
                <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-white/45">
                      <span className="tabular-nums text-white/65">{fmtShortDate(r.tx_date)}</span>
                      <span className="text-white/25">·</span>
                      <span className="rounded-full bg-emerald-500/15 px-1.5 py-px text-[9px] font-bold uppercase text-emerald-200">
                        {r.money_in?.kind_label ??
                          FUNDING_KIND_LABEL[r.kind] ??
                          r.kind}
                      </span>
                      {r.role === "account_self" && (
                        <span className="rounded-full bg-amber-500/15 px-1.5 py-px text-[9px] font-semibold uppercase text-amber-100">
                          payer ≠ origen
                        </span>
                      )}
                      {r.kind === "loan" && (
                        <span className="rounded-full bg-amber-500/20 px-1.5 py-px text-[9px] font-bold uppercase text-amber-100">
                          préstamo
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 break-words text-[13px] font-medium text-white/90">
                      {r.description}
                    </div>
                    {r.money_in?.narrative_es && (
                      <p className="mt-0.5 break-words text-[10px] leading-snug text-white/45">
                        {r.money_in.narrative_es}
                      </p>
                    )}
                    <div className="mt-0.5 font-mono text-[9px] text-white/30">{r.id}</div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="tabular-nums text-[14px] font-bold text-emerald-300">
                      +{fmtMoney(r.amount, "ARS")}
                    </div>
                    {r.email && (
                      <div
                        className={cn(
                          "max-w-[14rem] truncate text-[10px]",
                          r.role === "account_self" ? "text-amber-200/70" : "text-white/45",
                        )}
                        title={r.email}
                      >
                        {r.role === "buyer"
                          ? "comprador"
                          : r.role === "account_self"
                            ? "cuenta (no origen)"
                            : "payer"}{" "}
                        {r.email}
                      </div>
                    )}
                  </div>
                </div>
                {r.money_in && (
                  <div className="mt-2">
                    <MoneyInDetailBlock m={r.money_in} />
                  </div>
                )}
              </div>
            ))}
            {moneyInRows.length > 80 && (
              <div className="px-3 py-2 text-center text-[10px] text-white/35">
                +{moneyInRows.length - 80} ingresos más en el feed
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

/** Detalle legible: payer honesto, saldos, préstamo, método. Sin overflow horizontal. */
function TxDetailLines({ r }: { r: LedgerRow }) {
  const lines: Array<{ label: string; value: string; mono?: boolean; emphasize?: boolean; warn?: boolean }> = [];
  const isTransfer =
    r.accountTx?.activity === "transferencia" ||
    r.accountTx?.activity === "ingreso_dinero" ||
    /transferencia/i.test(r.transaction_type ?? "") ||
    r.operation_type === "money_transfer";

  if (r.is_loan) {
    const kindLabel = r.loan_kind ? LOAN_KIND_LABEL[r.loan_kind] ?? r.loan_kind : "Préstamo MP";
    lines.push({ label: "Préstamo", value: kindLabel, emphasize: true });
    if (r.merchant_key) lines.push({ label: "Merchant", value: r.merchant_key, mono: true });
    if (r.poi_sub_type) lines.push({ label: "POI sub", value: r.poi_sub_type, mono: true });
    if (r.poi_branch) lines.push({ label: "Branch", value: r.poi_branch, mono: true });
  }
  if (r.customer_email) {
    if (isTransfer || r.email_role === "payer_mp") {
      lines.push({
        label: "Payer (MP)",
        value: r.customer_email,
        warn: true,
      });
    } else if (r.email_role === "buyer" || (!r.accountTx && r.real && !r.is_loan)) {
      lines.push({ label: "Comprador", value: r.customer_email, emphasize: true });
    } else {
      lines.push({ label: "Email", value: r.customer_email });
    }
  }
  if (r.customer_name) lines.push({ label: "Nombre", value: r.customer_name });
  if (r.operation_type) lines.push({ label: "MP op", value: r.operation_type, mono: true });
  const methodLabel = humanMpMethod(r.payment_method_id);
  if (methodLabel) lines.push({ label: "Método", value: methodLabel });
  const typeLabel = humanMpMethod(r.payment_type_id);
  if (typeLabel && typeLabel !== methodLabel) {
    lines.push({ label: "Medio", value: typeLabel });
  }
  const flow = r.business_sub_unit ?? r.accountTx?.business_sub_unit;
  if (flow) lines.push({ label: "Flujo", value: flow, mono: true });
  const poi = r.poi_type ?? r.accountTx?.poi_type;
  if (poi) lines.push({ label: "POI", value: poi, mono: true });
  if (r.external_reference) {
    lines.push({ label: "Ref", value: r.external_reference, mono: true });
  }
  if (r.transaction_number) {
    lines.push({ label: "ID", value: r.transaction_number, mono: true });
  }
  if (lines.length === 0 && !r.note && !r.summary_es && r.wallet_balance_after == null) return null;
  const showSummary =
    Boolean(r.summary_es) &&
    r.summary_es !== r.description &&
    r.summary_es !== r.transaction_type;
  return (
    <div className="mt-1.5 min-w-0 space-y-1.5">
      {showSummary && (
        <p className="break-words text-[11px] leading-snug text-white/50">{r.summary_es}</p>
      )}
      {r.note && (
        <p className="break-words text-[11px] leading-snug text-amber-200/60">{r.note}</p>
      )}
      {(isTransfer || r.email_role === "payer_mp") && r.customer_email && (
        <p className="text-[10px] leading-snug text-amber-200/55">
          El email es <strong className="font-medium text-amber-100/80">payer de la API MP</strong>, no
          el CBU/alias destino de la transferencia.
        </p>
      )}
      {r.wallet_balance_after != null && r.currency === "ARS" && (
        <div className="rounded-lg border border-white/10 bg-white/[0.03] px-2.5 py-1.5 text-[11px]">
          <div className="text-[10px] font-medium uppercase tracking-wide text-white/40">
            Saldo en cuenta (estimado · extracto)
          </div>
          <div className="mt-0.5 flex flex-wrap items-baseline gap-x-3 gap-y-0.5 tabular-nums">
            {r.wallet_balance_before != null && (
              <span className="text-white/50">
                antes <span className="font-semibold text-white/75">{fmtMoney(r.wallet_balance_before, "ARS")}</span>
              </span>
            )}
            <span className="text-white/50">→</span>
            <span className="text-white/70">
              después{" "}
              <span
                className={cn(
                  "font-bold",
                  r.wallet_balance_after >= 0 ? "text-emerald-300/90" : "text-rose-300/90",
                )}
              >
                {fmtMoney(r.wallet_balance_after, "ARS")}
              </span>
            </span>
          </div>
        </div>
      )}
      {(r.money_in || (r.cashflow === "in" && r.real)) && r.money_in && (
        <MoneyInDetailBlock m={r.money_in} />
      )}
      {r.funding_trace && r.cashflow === "out" && (
        <FundingTraceBlock t={r.funding_trace} />
      )}
      {lines.length > 0 && (
        <dl className="grid grid-cols-1 gap-x-3 gap-y-0.5 text-[11px] leading-snug sm:grid-cols-2">
          {lines.map((l) => (
            <div key={l.label} className="flex min-w-0 gap-1.5">
              <dt className="shrink-0 text-white/35">{l.label}</dt>
              <dd
                className={cn(
                  "min-w-0 break-all",
                  l.warn
                    ? "font-medium text-amber-100/90"
                    : l.emphasize
                      ? "font-medium text-sky-200/95"
                      : "text-white/65",
                  l.mono && "font-mono text-[10px] text-white/50",
                )}
              >
                {l.value}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

/** Fila del libro como tarjeta (sin scroll horizontal). */
function LedgerRowCard({
  r,
  display,
  rate,
}: {
  r: LedgerRow;
  display: FinanzasCurrency;
  rate: number | null;
}) {
  const income = r.kind === "income";
  const badge = SOURCE_BADGE[r.origin] ?? SOURCE_BADGE.manual;
  const showNet = r.real && income && r.net != null && Math.abs(r.net - r.amount) > 0.5;
  const isMpFee = r.real && !income && r.category === "Comisiones";
  const isMp = r.origin === "mercadopago";
  const isAccount = Boolean(r.accountTx);
  const shown = inCurrency(r.amount, r.currency, display, rate);
  const amountLabel =
    shown != null
      ? `${income ? "+" : "−"}${fmtMoney(shown, display, 2)}`
      : `${income ? "+" : "−"}${fmtMoney(r.amount, r.currency, 2)}`;

  return (
    <article
      className={cn(
        "min-w-0 border-b border-white/[0.07] px-3 py-3 last:border-b-0 sm:px-4",
        r.real && "bg-white/[0.015]",
        isMp && income && "bg-sky-500/[0.04]",
        isMpFee && "bg-rose-500/[0.03]",
      )}
    >
      {/* Cabecera: fecha + badges · monto */}
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-white/50">
            <span className="font-medium tabular-nums text-white/70">
              {r.undated || !r.date ? (
                <span className="text-amber-300/80">sin fecha</span>
              ) : (
                fmtShortDate(r.date)
              )}
            </span>
            <span className="text-white/25">·</span>
            <span className="inline-flex items-center gap-1 text-white/55">
              <span aria-hidden>{badge.emoji}</span>
              {badge.label}
            </span>
            {r.account && (
              <>
                <span className="text-white/25">·</span>
                <span className="text-white/45">{r.account}</span>
              </>
            )}
          </div>
          <h3 className="mt-1 break-words text-[13px] font-semibold leading-snug text-white/92">
            {r.description}
          </h3>
          <div className="mt-1.5 flex flex-wrap gap-1">
            {r.real && (
              <span
                className={cn(
                  "rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide",
                  isMpFee
                    ? "bg-rose-500/15 text-rose-300"
                    : isAccount
                      ? "bg-white/10 text-white/70"
                      : isMp
                        ? "bg-sky-500/20 text-sky-100"
                        : "bg-emerald-500/15 text-emerald-300",
                )}
              >
                {isMpFee ? "fee mp" : isAccount ? "cuenta mp" : "mp real"}
              </span>
            )}
            {r.cashflow === "in" && (
              <span className="rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-emerald-300">
                entra
              </span>
            )}
            {r.cashflow === "out" && (
              <span className="rounded-full bg-rose-500/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-rose-300">
                sale
              </span>
            )}
            {r.is_loan && (
              <span
                className="rounded-full border border-amber-400/40 bg-amber-500/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-100"
                title="Desembolso de crédito MP (SHORT_LOAN / Mercado Crédito) — no es venta de marca"
              >
                🏦 préstamo
              </span>
            )}
            {r.transaction_type && (
              <span className="max-w-full break-words rounded-full border border-sky-400/25 bg-sky-500/10 px-1.5 py-0.5 text-[10px] font-medium text-sky-100/90">
                {r.transaction_type}
              </span>
            )}
            {r.category && (
              <span className="rounded-full bg-white/[0.06] px-1.5 py-0.5 text-[10px] text-white/70">
                {txCategoryEmoji(r.category)} {r.category}
              </span>
            )}
            {(r.origin === "api" || r.item) && !r.real && (
              <span className="rounded-full bg-fuchsia-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase text-fuchsia-200">
                API
              </span>
            )}
            {r.origin === "recurring" && (
              <span className="inline-flex items-center gap-0.5 rounded-full bg-violet-500/15 px-1.5 py-0.5 text-[9px] text-violet-200">
                <Repeat className="h-3 w-3" /> recurrente
              </span>
            )}
          </div>
        </div>

        <div className="shrink-0 text-right">
          <div
            className={cn(
              "tabular-nums text-[14px] font-bold leading-tight sm:text-[15px]",
              income ? "text-emerald-300" : "text-red-300",
            )}
          >
            {amountLabel}
          </div>
          {shown == null && r.currency !== display && (
            <div className="text-[10px] text-amber-300/70">sin cotización</div>
          )}
          {r.currency !== display && shown != null && (
            <div className="text-[10px] text-white/40">{fmtMoney(r.amount, r.currency, 2)}</div>
          )}
          {showNet && (
            <div className="text-[10px] text-white/45">neto {fmtMoney(r.net, r.currency, 2)}</div>
          )}
          {r.wallet_balance_after != null && r.currency === "ARS" && (
            <div
              className="mt-1 text-[10px] tabular-nums text-white/40"
              title="Saldo estimado en wallet ARS tras este movimiento (acumulado en ventana del feed)"
            >
              saldo {fmtMoney(r.wallet_balance_after, "ARS")}
            </div>
          )}
          <div className="mt-1.5 flex justify-end gap-0.5">
            <span
              title="Dato MercadoPago automático (read-only)"
              className="inline-flex items-center gap-1 text-[10px] text-white/30"
            >
              <RefreshCw className="h-3 w-3" />
              MP
            </span>
          </div>
        </div>
      </div>

      <TxDetailLines r={r} />
    </article>
  );
}

/** Ingresos reales del período: lo importante es el NETO de MRR y de one-pay. Compacto. */
function RealIncomeBreakdown({ realTx, mrr, display, rate }: { realTx: FinanzasRealTx[]; mrr: FinanzasMrr | null; display: FinanzasCurrency; rate: number | null }) {
  const hasMrr = mrr != null && mrr.activeCount > 0;
  if (!realTx.length && !hasMrr) return null;
  let subN = 0, subG = 0, oneN = 0, oneG = 0, mpDep = 0, mpComm = 0;
  for (const r of realTx) {
    const g = inCurrency(r.gross, r.currency, display, rate);
    if (g == null) continue;
    const n = inCurrency(r.net ?? r.gross, r.currency, display, rate) ?? g;
    if (r.is_subscription) { subN += n; subG += g; } else { oneN += n; oneG += g; }
    // MercadoPago: depósito = neto que entró; comisión (gasto real) = bruto − neto.
    if (r.source === "mercadopago") { mpDep += n; mpComm += Math.max(0, g - n); }
  }
  const items = [
    { l: "Suscripciones cobradas (período)", net: subN, gross: subG, Icon: Repeat, accent: "text-violet-200" },
    { l: "One-pay (pagos únicos)", net: oneN, gross: oneG, Icon: DollarSign, accent: "text-emerald-200" },
  ];
  const runRate = hasMrr
    ? [mrr.usd > 0 ? fmtMoney(mrr.usd, "USD") : null, mrr.ars > 0 ? fmtMoney(mrr.ars, "ARS") : null].filter(Boolean).join(" + ") || "—"
    : null;
  return (
    <div className="space-y-2">
      {realTx.length > 0 && (
        <div className="grid grid-cols-2 gap-3">
          {items.map((it) => (
            <div key={it.l} className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-3">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-white/45">
                <it.Icon className="h-3.5 w-3.5" />{it.l} · neto real
              </div>
              <div className={cn("mt-1 proportional-nums text-lg font-bold leading-none sm:text-xl", it.accent)}>{fmtMoney(it.net, display)}</div>
              <div className="mt-1 text-[10px] text-white/35">bruto {fmtMoney(it.gross, display)}</div>
            </div>
          ))}
        </div>
      )}
      {hasMrr && (
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 rounded-xl border border-violet-500/20 bg-violet-500/[0.04] px-3 py-2 text-[11px]">
          <span className="flex items-center gap-1 font-semibold uppercase tracking-wide text-violet-200/80"><Repeat className="h-3 w-3" />MRR activo (run-rate)</span>
          <span className="text-white/75">{mrr.activeCount} activa{mrr.activeCount === 1 ? "" : "s"}</span>
          <span className="text-white/30">·</span>
          <span className="proportional-nums text-white/80">run-rate <span className="font-semibold">{runRate}</span> <span className="text-white/40">/mes</span></span>
          <span className="text-white/30">({mrr.mpCount} MP)</span>
        </div>
      )}
      {mpComm > 0 && (
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 rounded-xl border border-sky-500/20 bg-sky-500/[0.04] px-3 py-2 text-[11px]">
          <span className="font-semibold uppercase tracking-wide text-sky-200/80">🔵 MercadoPago</span>
          <span className="text-white/75">depósitos <span className="proportional-nums font-semibold text-emerald-300">{fmtMoney(mpDep, display)}</span></span>
          <span className="text-white/30">·</span>
          <span className="text-white/75">comisión <span className="proportional-nums font-semibold text-rose-300">{fmtMoney(mpComm, display)}</span> <span className="text-white/40">(gasto real · filas fee en el libro)</span></span>
        </div>
      )}
      <div className="text-[10px] text-white/35">
        Libro = <strong className="text-white/50">solo MercadoPago</strong> (sin altas manuales ni diario API)
        {realTx.length > 0 ? <> · <span className="text-white/50">no es extracto bancario live</span></> : null}
        {mpComm > 0 ? <> · comisiones MP como <span className="text-rose-300/80">gasto</span></> : null}
        {" · "}cobros de marca = ingreso; transferencias/compras de cuenta = <span className="text-white/50">cuenta mp</span>.
      </div>
    </div>
  );
}

function TxSummaryCards({
  txs,
  display,
  rate,
}: {
  txs: Pick<LedgerRow, "kind" | "amount" | "currency" | "category" | "item" | "origin" | "is_loan">[];
  display: FinanzasCurrency;
  rate: number | null;
}) {
  let ingresos = 0, gastos = 0, anyUnconv = false;
  let prestamos = 0;
  let nPrestamos = 0;
  const byCat: Record<string, number> = {};
  for (const t of txs) {
    if (t.is_loan) {
      const lv = inCurrency(t.amount, t.currency, display, rate);
      if (lv != null) {
        prestamos += lv;
        nPrestamos += 1;
      }
    }
    if (!ledgerAffectsBalance(t)) continue;
    const v = inCurrency(t.amount, t.currency, display, rate);
    if (v == null) { anyUnconv = true; continue; }
    if (t.kind === "income") ingresos += v;
    else { gastos += v; const key = t.category || "Otros"; byCat[key] = (byCat[key] ?? 0) + v; }
  }
  const balance = ingresos - gastos;
  const topCat = Object.entries(byCat).sort((a, b) => b[1] - a[1])[0] ?? null;
  const cards = [
    { l: "Balance", v: balance, sub: `${txs.length} mov. · sin préstamos ni transfer`, Icon: Wallet, card: balance >= 0 ? "from-emerald-500/[0.10] border-emerald-500/20" : "from-red-500/[0.10] border-red-500/20", icon: balance >= 0 ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400", val: balance >= 0 ? "text-emerald-300" : "text-red-300" },
    { l: "Gastos", v: gastos, sub: "cuenta MP + fees", Icon: TrendingDown, card: "from-red-500/[0.10] border-red-500/20", icon: "bg-red-500/15 text-red-400", val: "text-red-300" },
    { l: "Ingresos", v: ingresos, sub: "cobros MP (marca)", Icon: TrendingUp, card: "from-emerald-500/[0.10] border-emerald-500/20", icon: "bg-emerald-500/15 text-emerald-400", val: "text-emerald-300" },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {cards.map((k) => (
        <div key={k.l} className={cn("rounded-2xl border bg-gradient-to-br to-transparent p-4 shadow-sm", k.card)}>
          <div className="flex items-center gap-2.5">
            <div className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-xl", k.icon)}><k.Icon className="h-[18px] w-[18px]" /></div>
            <div className="text-[11px] font-medium uppercase tracking-wide text-white/45">{k.l}</div>
          </div>
          <div className={cn("mt-3 proportional-nums tracking-tight text-lg font-bold leading-none sm:text-[1.4rem]", k.val)}>{fmtMoney(k.v, display)}</div>
          <div className="mt-1.5 text-[11px] text-white/40">{anyUnconv && k.l === "Balance" ? "excl. sin cotización" : k.sub}</div>
        </div>
      ))}
      {nPrestamos > 0 ? (
        <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.12] to-transparent p-4 shadow-sm">
          <div className="text-[11px] font-medium uppercase tracking-wide text-amber-200/70">Préstamos (desembolso)</div>
          <div className="mt-2 proportional-nums text-lg font-bold text-amber-100 sm:text-[1.4rem]">{fmtMoney(prestamos, display)}</div>
          <div className="mt-1.5 text-[11px] text-white/40">
            {nPrestamos} mov. · no suman al balance (deuda)
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-amber-500/20 bg-gradient-to-br from-amber-500/[0.10] to-transparent p-4 shadow-sm">
          <div className="text-[11px] font-medium uppercase tracking-wide text-white/45">Categoría con más gastos</div>
          <div className="mt-2 flex items-center gap-1.5 text-base font-semibold text-white/90">
            <span>{topCat ? txCategoryEmoji(topCat[0]) : "—"}</span>
            <span className="truncate">{topCat ? topCat[0] : "Sin gastos"}</span>
          </div>
          <div className="mt-1 proportional-nums text-[13px] font-bold text-amber-300">{topCat ? fmtMoney(topCat[1], display) : "—"}</div>
        </div>
      )}
    </div>
  );
}

/** Filtros del extracto MP (sin manual / sin diario API). */
type LedgerSourceFilter = "all" | "cobros" | "cuenta" | "prestamo" | "fees";

function matchesSourceFilter(r: LedgerRow, f: LedgerSourceFilter): boolean {
  if (f === "all") return true;
  if (f === "prestamo") return Boolean(r.is_loan);
  if (f === "fees") return r.real && r.kind === "expense" && r.category === "Comisiones";
  if (f === "cobros") return r.real && Boolean(r.realTx) && !r.is_loan;
  if (f === "cuenta") return Boolean(r.accountTx);
  return true;
}

export function TransaccionesTab({
  data,
  today,
  isFetching,
  onRefresh,
}: {
  data: FinanzasData;
  today: Date;
  isFetching?: boolean;
  onRefresh?: () => void;
}) {
  const { rate, source, isLoading: rateLoading } = useFinanzasRate(data.meta);
  const [display, setDisplay] = useState<FinanzasCurrency>("ARS");
  const [cursor, setCursor] = useState<{ y: number; m: number } | null>({ y: today.getFullYear(), m: today.getMonth() });
  const [sourceFilter, setSourceFilter] = useState<LedgerSourceFilter>("all");
  const [q, setQ] = useState("");
  /** Solo operaciones con monto ≥ $1.000.000 ARS (USD se convierte con cotización). */
  const [overMillionOnly, setOverMillionOnly] = useState(false);

  // ── Solo MercadoPago (feed real). Sin finanzas_transactions manual ni diario API. ──
  const realTxAll = data.real?.incomeTransactions ?? EMPTY_REAL_TX;
  const accountTxAll = data.real?.accountActivity ?? EMPTY_ACCOUNT_TX;
  const range = useMemo(() => {
    if (!cursor) return null;
    const mm = String(cursor.m + 1).padStart(2, "0");
    const lastDay = new Date(cursor.y, cursor.m + 1, 0).getDate();
    return { first: `${cursor.y}-${mm}-01`, last: `${cursor.y}-${mm}-${String(lastDay).padStart(2, "0")}` };
  }, [cursor]);
  const realTx = useMemo(
    () => (range ? realTxAll.filter((r) => r.tx_date >= range.first && r.tx_date <= range.last) : realTxAll),
    [realTxAll, range],
  );
  /** Cobros de marca en toda la ventana del feed (excluye préstamos del income feed). */
  const brandCobrosAll = useMemo(
    () => realTxAll.filter((r) => !r.is_loan).length,
    [realTxAll],
  );
  // Extracto 100% MP: cobros de marca + comisiones + actividad de cuenta.
  const allRows = useMemo(() => {
    const inR = (d: string) => {
      if (!range) return true;
      if (!d) return false;
      return d >= range.first && d <= range.last;
    };
    const realInRange = realTxAll.filter((r) => inR(r.tx_date));
    const merged: LedgerRow[] = [
      ...realInRange.map(realToLedger),
      ...realInRange.map(mpCommissionToLedger).filter((x): x is LedgerRow => x != null),
      ...accountTxAll.filter((r) => inR(r.tx_date)).map(accountToLedger),
    ];
    return merged.sort((a, b) => {
      if (a.date === b.date) return a.key < b.key ? 1 : -1;
      return a.date < b.date ? 1 : -1;
    });
  }, [realTxAll, accountTxAll, range]);

  /** Monto de la fila en ARS (umbral $1M). USD → ARS con cotización; sin rate, USD no califica. */
  const rowAmountArs = (r: LedgerRow, fx: number | null): number | null => {
    if (r.currency === "ARS") return Math.abs(r.amount);
    return inCurrency(Math.abs(r.amount), r.currency, "ARS", fx);
  };
  const OVER_MILLION_ARS = 1_000_000;

  const overMillionCount = useMemo(() => {
    return allRows.filter((r) => {
      const ars = rowAmountArs(r, rate);
      return ars != null && ars >= OVER_MILLION_ARS;
    }).length;
  }, [allRows, rate]);

  const rows = useMemo(() => {
    let base = allRows.filter((r) => matchesSourceFilter(r, sourceFilter));
    if (overMillionOnly) {
      base = base.filter((r) => {
        const ars = rowAmountArs(r, rate);
        return ars != null && ars >= OVER_MILLION_ARS;
      });
    }
    const needle = q.trim().toLowerCase();
    if (!needle) return base;
    return base.filter((r) => {
      const hay = [
        r.description,
        r.note,
        r.category,
        r.account,
        r.customer_email,
        r.customer_name,
        r.transaction_type,
        r.operation_type,
        r.payment_method_id,
        r.transaction_number,
        r.external_reference,
        r.summary_es,
        r.business_sub_unit,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [allRows, sourceFilter, q, overMillionOnly, rate]);

  const counts = useMemo(() => countMpExtractRows(allRows), [allRows]);
  /** Cobros de marca en el período (excluye préstamos SHORT_LOAN del feed income). */
  const cobrosInPeriod = useMemo(
    () => realTx.filter((r) => !r.is_loan).length,
    [realTx],
  );

  const shiftMonth = (delta: number) => setCursor((c) => {
    const base = c ?? { y: today.getFullYear(), m: today.getMonth() };
    const n = base.m + delta;
    return { y: base.y + Math.floor(n / 12), m: ((n % 12) + 12) % 12 };
  });
  const monthLabel = cursor
    ? capitalize(new Intl.DateTimeFormat("es-AR", { month: "long", year: "numeric" }).format(new Date(cursor.y, cursor.m, 1)))
    : "Todas las fechas";

  const filterChips: { id: LedgerSourceFilter; label: string; count: number; accent?: string }[] = [
    { id: "all", label: "Todos MP", count: counts.all },
    { id: "cobros", label: "💰 Cobros", count: counts.cobros, accent: "border-sky-500/40 bg-sky-500/10 text-sky-100" },
    { id: "cuenta", label: "📒 Cuenta", count: counts.cuenta, accent: "border-white/20 bg-white/[0.06] text-white/80" },
    {
      id: "prestamo",
      label: "🏦 Préstamos",
      count: counts.prestamo,
      accent: "border-amber-400/50 bg-amber-500/15 text-amber-100",
    },
    { id: "fees", label: "Fee MP", count: counts.fees, accent: "border-rose-500/40 bg-rose-500/10 text-rose-100" },
  ];

  return (
    <div className="min-w-0 space-y-4 overflow-x-hidden">
      {/* Cabecera: solo extracto MercadoPago */}
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-3 rounded-2xl border border-sky-500/20 bg-gradient-to-br from-sky-500/[0.08] to-transparent px-3 py-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2 text-[12px] font-semibold text-sky-100/90">
            <span className={cn("h-2 w-2 rounded-full", isFetching ? "animate-pulse bg-amber-400" : "bg-emerald-400")} />
            💸 Extracto MercadoPago · sin manuales
          </div>
          <p className="max-w-2xl text-[11px] leading-relaxed text-white/45">
            <strong className="text-sky-200/90">Solo movimientos reales de MP</strong> (cobros de marca +
            extracto de cuenta + fees). Webhook/sync → Postgres.{" "}
            <strong className="text-white/70">No hay altas manuales ni diario API</strong> en esta vista.
            Stripe y revenue de producto viven en el admin de negocio.
          </p>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-white/50">
            <span>
              Cobros período:{" "}
              <span className="font-semibold tabular-nums text-sky-200">
                {cobrosInPeriod}
              </span>
              {realTx.filter((r) => r.is_loan).length > 0 && (
                <span className="text-amber-200/60">
                  {" "}
                  · {realTx.filter((r) => r.is_loan).length} préstamo
                  {realTx.filter((r) => r.is_loan).length === 1 ? "" : "s"}
                </span>
              )}
            </span>
            <span className="text-white/25">·</span>
            <span>
              Cuenta:{" "}
              <span className="font-semibold tabular-nums text-white/70">{counts.cuenta}</span>
            </span>
            {data.real?.generatedAt && (
              <>
                <span className="text-white/25">·</span>
                <span className="tabular-nums" title={data.real.generatedAt}>
                  feed {fmtRelativeTime(Date.parse(data.real.generatedAt))}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {onRefresh && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={isFetching}
              onClick={onRefresh}
              className="h-8 border-sky-500/30 text-[12px] text-sky-100"
              title="Releer histórico desde Postgres (no llama a MercadoPago HTTP)"
            >
              <RefreshCw className={cn("mr-1.5 h-3.5 w-3.5", isFetching && "animate-spin")} />
              {isFetching ? "Leyendo DB…" : "Actualizar DB"}
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/[0.03] p-0.5">
            <button type="button" aria-label="Mes anterior" onClick={() => shiftMonth(-1)} className="rounded-md p-1 text-white/50 hover:bg-white/10 hover:text-white"><ChevronLeft className="h-4 w-4" /></button>
            <span className="min-w-[8.5rem] text-center text-[12px] font-semibold text-white/80">{monthLabel}</span>
            <button type="button" aria-label="Mes siguiente" onClick={() => shiftMonth(1)} className="rounded-md p-1 text-white/50 hover:bg-white/10 hover:text-white"><ChevronRight className="h-4 w-4" /></button>
          </div>
          <button type="button" onClick={() => setCursor(cursor ? null : { y: today.getFullYear(), m: today.getMonth() })}
            className={cn("rounded-lg border px-2.5 py-1 text-[12px] font-medium transition", cursor ? "border-white/10 text-white/50 hover:text-white/80" : "border-violet-500/40 bg-violet-500/15 text-violet-200")}>
            {cursor ? "Ver todas" : "Filtrar mes"}
          </button>
        </div>
        <CurrencyToggle value={display} onChange={setDisplay} />
      </div>

      {/* Filtro por origen + umbral $1M + búsqueda */}
      <div className="flex min-w-0 flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Filtrar por origen">
            {filterChips.map((chip) => {
              const active = sourceFilter === chip.id;
              return (
                <button
                  key={chip.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setSourceFilter(chip.id)}
                  className={cn(
                    "rounded-full border px-2.5 py-1 text-[11px] font-medium transition",
                    active
                      ? chip.accent ?? "border-white/25 bg-white/10 text-white"
                      : "border-white/10 bg-white/[0.02] text-white/45 hover:border-white/20 hover:text-white/70",
                  )}
                >
                  {chip.label}
                  <span className={cn("ml-1 tabular-nums", active ? "opacity-90" : "opacity-50")}>{chip.count}</span>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            aria-pressed={overMillionOnly}
            title={
              rate == null
                ? "Montos en ARS ≥ $1.000.000 (USD se excluyen sin cotización)"
                : "Montos ≥ $1.000.000 ARS (USD convertido con cotización)"
            }
            onClick={() => {
              setOverMillionOnly((v) => {
                const next = !v;
                // Al activar: ver todo el feed, no solo el mes actual
                if (next) setCursor(null);
                return next;
              });
            }}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition",
              overMillionOnly
                ? "border-amber-400/50 bg-amber-500/20 text-amber-100 shadow-[0_0_0_1px_rgba(251,191,36,0.15)]"
                : "border-amber-500/25 bg-amber-500/[0.06] text-amber-200/80 hover:border-amber-400/40 hover:bg-amber-500/12 hover:text-amber-100",
            )}
          >
            <span aria-hidden className="text-[12px]">💎</span>
            <span>&gt; $1M</span>
            <span
              className={cn(
                "rounded-md px-1.5 py-px tabular-nums text-[10px] font-bold",
                overMillionOnly ? "bg-amber-400/25 text-amber-50" : "bg-white/10 text-amber-100/70",
              )}
            >
              {overMillionCount}
            </span>
          </button>
          <div className="relative min-w-[200px] flex-1 sm:max-w-xs">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/35" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar email, tipo, ID, transferencia…"
              className={cn(inputCls, "h-8 pl-8 pr-8 text-[12px]")}
              aria-label="Buscar transacciones"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ("")}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-white/40 hover:text-white/80"
                aria-label="Limpiar búsqueda"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
        {overMillionOnly && (
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-500/25 bg-amber-500/[0.07] px-3 py-2 text-[11px] text-amber-100/85">
            <span className="font-semibold text-amber-100">Filtro activo:</span>
            <span>
              solo operaciones ≥ <strong className="tabular-nums">$1.000.000 ARS</strong>
              {rate != null ? " (USD convertido)" : " (solo ARS; sin cotización USD)"}
            </span>
            <span className="text-amber-200/50">·</span>
            <span className="tabular-nums">
              {rows.length} resultado{rows.length === 1 ? "" : "s"}
            </span>
            {!cursor && <span className="text-amber-200/50">· todas las fechas del feed</span>}
            <button
              type="button"
              onClick={() => setOverMillionOnly(false)}
              className="ml-auto rounded-md border border-amber-400/30 px-2 py-0.5 text-[10px] font-medium text-amber-100 hover:bg-amber-400/15"
            >
              Quitar
            </button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-1.5 text-[11px] text-white/40">
        <RefreshCw className={cn("h-3 w-3", (rateLoading || isFetching) && "animate-spin")} />
        {rate != null
          ? <>Dólar Oficial <span className="font-semibold text-white/70">{fmtNum(rate)} ARS</span> · {source === "live" ? "en vivo" : "override manual"}</>
          : "Cotización no disponible — cargá un override en Editar para convertir USD."}
        <span className="text-white/25">
          · MP HTTP solo en webhook · panel = DB
          {data.real?.sourceOfTruth === "postgres" ? " · source: postgres" : ""}
          {data.real?.hitExternalApis === false ? " · 0 external APIs" : ""}
        </span>
      </div>

      <TxSummaryCards txs={rows} display={display} rate={rate} />

      <p className="rounded-lg border border-sky-500/15 bg-sky-500/[0.04] px-3 py-2 text-[11px] text-sky-100/70">
        Capacidad mes a mes y “¿de dónde vino la plata?” están en el tab{" "}
        <strong className="text-sky-100">🔗 Trazabilidad</strong> (solo MercadoPago).
      </p>

      <RealIncomeBreakdown realTx={realTx} mrr={data.real?.mrr ?? null} display={display} rate={rate} />

      <Card className="min-w-0 overflow-hidden border-white/10 bg-white/[0.02]">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-white/10 px-3 py-2.5 text-[11px] text-white/40 sm:px-4">
          <span className="min-w-0 break-words">
            {rows.length} movimiento{rows.length === 1 ? "" : "s"}
            {sourceFilter !== "all" && <span className="text-white/30"> · filtro {sourceFilter}</span>}
            {overMillionOnly && <span className="font-medium text-amber-200/80"> · ≥ $1M</span>}
            {q.trim() && <span className="text-white/30"> · “{q.trim()}”</span>}
            {cobrosInPeriod > 0 && sourceFilter === "all" && !q.trim() && (
              <span className="text-sky-300/70">
                {" "}
                · {cobrosInPeriod} cobro{cobrosInPeriod === 1 ? "" : "s"} MP
              </span>
            )}
            {counts.prestamo > 0 && sourceFilter === "all" && !q.trim() && (
              <span className="text-amber-200/70"> · {counts.prestamo} préstamo{counts.prestamo === 1 ? "" : "s"}</span>
            )}
            {counts.cuenta > 0 && sourceFilter === "all" && !q.trim() && (
              <span className="text-white/35"> · {counts.cuenta} cuenta</span>
            )}
          </span>
          {range && (
            <span className="shrink-0 text-white/40">
              {fmtShortDate(range.first)} → {fmtShortDate(range.last)}
            </span>
          )}
        </div>

        {rows.length === 0 ? (
          <div className="px-4 py-12 text-center text-white/35">
            <Search className="mx-auto mb-2 h-5 w-5 opacity-50" />
            {sourceFilter === "cobros" ? (
              <>
                Sin cobros de marca MP en este período.
                <div className="mx-auto mt-2 max-w-md text-[11px] text-white/30">
                  Probá “Ver todas”, “Actualizar DB”, o revisá webhook/sync en{" "}
                  <code className="text-white/40">manual_transactions</code>
                  {brandCobrosAll > 0 ? ` · hay ${brandCobrosAll} cobros en el feed` : ""}.
                </div>
              </>
            ) : sourceFilter === "cuenta" ? (
              <>Sin movimientos de extracto de cuenta MP en este período.</>
            ) : sourceFilter === "prestamo" ? (
              <>
                Sin desembolsos de préstamo MP en este período.
                <div className="mx-auto mt-2 max-w-md text-[11px] text-white/30">
                  Se detectan por POI <code className="text-white/40">SHORT_LOAN</code>, branch “Short Loan -
                  Outlay”, ref <code className="text-white/40">loan-*</code> o payer
                  creditos@mercadopago.com.
                </div>
              </>
            ) : sourceFilter === "fees" ? (
              <>Sin comisiones MP (bruto − neto) en este período.</>
            ) : q.trim() ? (
              <>Ningún movimiento MP coincide con “{q.trim()}”.</>
            ) : (
              <>Sin movimientos MercadoPago en este período.</>
            )}
          </div>
        ) : (
          <div className="min-w-0 divide-y divide-white/[0.04]">
            {rows.map((r) => (
              <LedgerRowCard key={r.key} r={r} display={display} rate={rate} />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ── VIEW: Recurrentes Oruon (ingresos + gastos fijos, calendario) ─────────────────
type RecKindFilter = "all" | "income" | "expense";

interface RecForm {
  id: number; name: string; kind: TxKind; amount: string; currency: FinanzasCurrency;
  category: string; frequency: RecurringFrequency; day_of_month: string; next_date: string;
  icon: string; active: boolean; note: string;
}
const emptyRecForm = (preset?: Partial<RecForm>): RecForm => ({
  id: nextNegId(), name: "", kind: "expense", amount: "", currency: "ARS", category: "otros",
  frequency: "monthly", day_of_month: "1", next_date: "", icon: "", active: true, note: "", ...preset,
});
const recToForm = (r: FinanzasRecurring): RecForm => ({
  id: r.id, name: r.name, kind: r.kind, amount: String(r.amount ?? ""), currency: r.currency,
  category: r.category ?? "otros", frequency: r.frequency, day_of_month: r.day_of_month != null ? String(r.day_of_month) : "",
  next_date: r.next_date ?? "", icon: r.icon ?? "", active: r.active, note: r.note ?? "",
});

function isoOnMonthDay(year: number, month0: number, day: number): string {
  const dim = new Date(year, month0 + 1, 0).getDate();
  const d = Math.min(Math.max(1, day), dim);
  return `${year}-${String(month0 + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function RecFormCard({ form, setForm, onSave, onCancel, saving }: {
  form: RecForm; setForm: (f: RecForm) => void; onSave: (f: RecForm) => void; onCancel: () => void; saving: boolean;
}) {
  const upd = (patch: Partial<RecForm>) => setForm({ ...form, ...patch });
  const cats = RECURRING_CATEGORIES.filter((c) => c.key !== "todas");
  const title =
    form.id < 0
      ? form.kind === "income" ? "Nuevo ingreso recurrente" : "Nuevo gasto fijo"
      : form.kind === "income" ? "Editar ingreso recurrente" : "Editar gasto fijo";
  return (
    <Card className="border-violet-500/25 bg-violet-500/[0.04]">
      <CardHeader className="pb-2"><CardTitle className="text-sm text-white/85">{title}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          {(["income", "expense"] as TxKind[]).map((k) => (
            <button key={k} type="button" onClick={() => upd({ kind: k, category: k === "income" ? "ingresos" : form.category === "ingresos" ? "otros" : form.category })}
              className={cn("flex-1 rounded-lg border px-3 py-2 text-[13px] font-semibold transition",
                form.kind === k ? (k === "income" ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-200" : "border-red-500/40 bg-red-500/15 text-red-200") : "border-white/10 text-white/45 hover:text-white/70")}>
              {k === "income" ? "＋ Ingreso / revenue" : "－ Gasto fijo"}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Field label="Nombre" className="col-span-2">
            <input value={form.name} onChange={(e) => upd({ name: e.target.value })}
              placeholder={form.kind === "income" ? "Ej. Cobro fijo mensual" : "Ej. Alquiler"} className={inputCls} />
          </Field>
          <Field label="Icono (emoji)"><input value={form.icon} onChange={(e) => upd({ icon: e.target.value })} placeholder={form.kind === "income" ? "💵" : "🏠"} className={inputCls} /></Field>
          <Field label="Categoría">
            <select value={form.category} onChange={(e) => upd({ category: e.target.value })} className={inputCls}>
              {cats.map((c) => <option key={c.key} value={c.key}>{c.emoji} {c.label}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Field label="Monto"><input type="number" inputMode="decimal" value={form.amount} onChange={(e) => upd({ amount: e.target.value })} placeholder="0" className={inputCls} /></Field>
          <Field label="Moneda"><select value={form.currency} onChange={(e) => upd({ currency: e.target.value as FinanzasCurrency })} className={inputCls}><option value="ARS">ARS</option><option value="USD">USD</option></select></Field>
          <Field label="Frecuencia"><select value={form.frequency} onChange={(e) => upd({ frequency: e.target.value as RecurringFrequency })} className={inputCls}><option value="monthly">Mensual</option><option value="weekly">Semanal</option><option value="yearly">Anual</option></select></Field>
          {form.frequency === "monthly"
            ? (
              <Field label="Día del mes">
                <input type="number" min={1} max={31} value={form.day_of_month} onChange={(e) => upd({ day_of_month: e.target.value })} className={inputCls} />
                <div className="mt-1 text-[10px] text-white/35">Aparece ese día en el calendario. También podés clickear un día del mes.</div>
              </Field>
            )
            : <Field label="Próxima fecha"><input type="date" value={form.next_date} onChange={(e) => upd({ next_date: e.target.value })} className={inputCls} /></Field>}
        </div>
        <Field label="Nota"><input value={form.note} onChange={(e) => upd({ note: e.target.value })} placeholder="Opcional" className={inputCls} /></Field>
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-[12px] text-white/60">
            <input type="checkbox" checked={form.active} onChange={(e) => upd({ active: e.target.checked })} className="h-3.5 w-3.5 accent-emerald-500" />
            Activa (aparece en el calendario)
          </label>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving}><X className="mr-1 h-3.5 w-3.5" />Cancelar</Button>
            <Button size="sm" onClick={() => onSave(form)} disabled={saving}>{saving ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1 h-3.5 w-3.5" />}Guardar</Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const WEEKDAYS = ["lu", "ma", "mi", "ju", "vi", "sá", "do"];

const KIND_FILTERS: { key: RecKindFilter; label: string; emoji: string }[] = [
  { key: "all", label: "Todas", emoji: "✨" },
  { key: "income", label: "Ingresos (revenue)", emoji: "💵" },
  { key: "expense", label: "Gastos fijos", emoji: "🏠" },
];

function RecListRow({
  r, today, onEdit, onToggle, onRemove, onValidate, validating,
}: {
  r: FinanzasRecurring; today: Date;
  onEdit: (r: FinanzasRecurring) => void;
  onToggle: (r: FinanzasRecurring) => void;
  onRemove: (r: FinanzasRecurring) => void;
  /** null pending seed → Sí = recurrente, No = gasto único */
  onValidate?: (r: FinanzasRecurring, isRecurring: boolean) => void;
  validating?: boolean;
}) {
  const pending = isRecurringPendingValidation(r);
  return (
    <div
      className={cn(
        "group relative flex items-center gap-3 rounded-xl border p-3 transition-colors",
        pending
          ? "border-orange-500/45 bg-orange-500/[0.07] hover:border-orange-400 hover:bg-orange-500/[0.16] hover:shadow-[0_0_0_1px_rgba(251,146,60,0.45)]"
          : "border-white/10 bg-white/[0.02] hover:border-white/15 hover:bg-white/[0.04]",
      )}
    >
      <div
        className={cn(
          "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-lg",
          pending ? "bg-orange-500/20 ring-1 ring-orange-400/40 group-hover:bg-orange-500/30" : "bg-white/[0.06]",
        )}
      >
        {r.icon || recurringCategoryMeta(r.category).emoji}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn("truncate text-[13px] font-semibold", pending ? "text-orange-100 group-hover:text-orange-50" : "text-white/90")}>
            {r.name}
          </span>
          <span className={cn("rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase", r.kind === "income" ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300")}>
            {r.kind === "income" ? "Ingreso" : "Gasto fijo"}
          </span>
          {pending && (
            <span className="rounded-full bg-orange-500/25 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-orange-200 ring-1 ring-orange-400/40 group-hover:bg-orange-500/40 group-hover:text-orange-50">
              Sin validar
            </span>
          )}
          {!r.active && <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-[9px] text-white/40">pausada</span>}
          {r.frequency === "monthly" && r.day_of_month != null && (
            <span className="rounded-full bg-sky-500/10 px-1.5 py-0.5 text-[9px] font-medium text-sky-300/80">día {r.day_of_month}</span>
          )}
        </div>
        <div className={cn("mt-0.5 text-[11px]", pending ? "text-orange-200/70 group-hover:text-orange-100/90" : "text-white/45")}>
          {FREQUENCY_LABELS[r.frequency]} · próx. {fmtShortDate(nextOccurrence(r, today))}
          {r.note ? ` · ${r.note}` : ""}
          {pending ? " · ¿Es recurrente o un gasto único?" : ""}
        </div>
        {/* Sí/No: always visible when pending; stronger on hover */}
        {pending && onValidate && (
          <div className="mt-2 flex flex-wrap items-center gap-2 opacity-90 transition group-hover:opacity-100">
            <span className="text-[10px] font-medium uppercase tracking-wide text-orange-300/80 group-hover:text-orange-200">
              Validar
            </span>
            <button
              type="button"
              disabled={validating}
              onClick={() => onValidate(r, true)}
              className="rounded-md border border-emerald-500/40 bg-emerald-500/15 px-2.5 py-1 text-[11px] font-semibold text-emerald-200 transition hover:border-emerald-400/60 hover:bg-emerald-500/25 disabled:opacity-50"
            >
              Sí · recurrente
            </button>
            <button
              type="button"
              disabled={validating}
              onClick={() => onValidate(r, false)}
              className="rounded-md border border-orange-400/50 bg-orange-500/20 px-2.5 py-1 text-[11px] font-semibold text-orange-100 transition hover:border-orange-300 hover:bg-orange-500/35 disabled:opacity-50"
            >
              No · es un gasto
            </button>
          </div>
        )}
      </div>
      <div className="text-right">
        <div className={cn(
          "proportional-nums text-[13px] font-bold",
          pending ? "text-orange-200 group-hover:text-orange-100" : r.kind === "income" ? "text-emerald-300" : "text-white/85",
        )}>
          {fmtMoney(r.amount, r.currency)}
        </div>
        <div className="mt-1 flex justify-end gap-0.5">
          <button type="button" title={r.active ? "Pausar" : "Activar"} onClick={() => onToggle(r)} className={cn("rounded p-1 hover:bg-white/10", r.active ? "text-white/40" : "text-emerald-300/70")}><Power className="h-3.5 w-3.5" /></button>
          <button type="button" title="Editar" onClick={() => onEdit(r)} className="rounded p-1 text-white/40 hover:bg-white/10 hover:text-white"><Pencil className="h-3.5 w-3.5" /></button>
          <button type="button" title="Eliminar" onClick={() => onRemove(r)} className="rounded p-1 text-white/40 hover:bg-red-500/15 hover:text-red-300"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      </div>
    </div>
  );
}

function RecurrentesTab({ data, today }: { data: FinanzasData; today: Date }) {
  const save = useSaveFinanzas();
  const { toast } = useToast();
  const { rate } = useFinanzasRate(data.meta);
  const [display, setDisplay] = useState<FinanzasCurrency>("ARS");
  const [kindFilter, setKindFilter] = useState<RecKindFilter>("all");
  const [cat, setCat] = useState<string>("todas");
  const [cursor, setCursor] = useState({ y: today.getFullYear(), m: today.getMonth() });
  const [form, setForm] = useState<RecForm | null>(null);

  const allRecs = data.recurring ?? [];
  const pendingValidation = useMemo(
    () => allRecs.filter((r) => isRecurringPendingValidation(r)),
    [allRecs],
  );
  const filtered = useMemo(() => allRecs.filter((r) => {
    if (kindFilter !== "all" && r.kind !== kindFilter) return false;
    if (cat !== "todas" && (r.category ?? "otros") !== cat) return false;
    return true;
  }), [allRecs, kindFilter, cat]);
  // Pendientes de validación primero (seed naranja arriba).
  const sortPendingFirst = (rows: FinanzasRecurring[]) =>
    [...rows].sort((a, b) => {
      const ap = isRecurringPendingValidation(a) ? 0 : 1;
      const bp = isRecurringPendingValidation(b) ? 0 : 1;
      if (ap !== bp) return ap - bp;
      return (a.sort_order ?? 0) - (b.sort_order ?? 0);
    });
  const incomeRecs = useMemo(
    () => sortPendingFirst(filtered.filter((r) => r.kind === "income")),
    [filtered],
  );
  const expenseRecs = useMemo(
    () => sortPendingFirst(filtered.filter((r) => r.kind === "expense")),
    [filtered],
  );
  // Calendario: solo activas del filtro actual (tipo ∩ categoría).
  // Pendientes de validación no entran al calendario hasta Sí (recurrente).
  const activeRecs = useMemo(
    () => filtered.filter((r) => r.active && !isRecurringPendingValidation(r)),
    [filtered],
  );
  // KPIs: only validated + active (seed hardcode no distorsiona hasta confirmar).
  const validatedActive = useMemo(
    () => allRecs.filter((r) => r.active && !isRecurringPendingValidation(r)),
    [allRecs],
  );

  const totals = useMemo(() => {
    // Totales solo sobre recurrentes YA validadas (seed hardcode no cuenta hasta Sí).
    let inc = 0, exp = 0, nInc = 0, nExp = 0;
    for (const r of validatedActive) {
      const v = inCurrency(monthlyEquivalent(r), r.currency, display, rate);
      if (v == null) continue;
      if (r.kind === "income") { inc += v; nInc++; } else { exp += v; nExp++; }
    }
    return { inc, exp, net: inc - exp, nInc, nExp, nActive: nInc + nExp };
  }, [validatedActive, display, rate]);

  const upcomingItems = useMemo((): AdminUpcomingItem[] => {
    const active = allRecs.filter((r) => r.active && !isRecurringPendingValidation(r));
    const rows = active
      .map((r) => {
        const whenIso = nextOccurrence(r, today);
        if (!whenIso) return null;
        const amt = inCurrency(r.amount, r.currency, display, rate) ?? r.amount;
        return {
          id: r.id,
          title: r.name,
          when: fmtShortDate(whenIso),
          whenIso,
          meta: fmtMoney(amt, display),
          status: r.kind === "income" ? "Ingreso" : "Gasto fijo",
          statusTone: (r.kind === "income" ? "good" : "warn") as AdminUpcomingItem["statusTone"],
          icon: r.icon || (r.kind === "income" ? "💵" : recurringCategoryMeta(r.category).emoji),
          rec: r,
        };
      })
      .filter((x): x is NonNullable<typeof x> => x != null)
      .sort((a, b) => (a.whenIso < b.whenIso ? -1 : a.whenIso > b.whenIso ? 1 : 0));
    return rows.map(({ rec: _r, whenIso: _w, ...item }) => item);
  }, [allRecs, today, display, rate]);

  const cal = useMemo(() => {
    const startWeekday = (new Date(cursor.y, cursor.m, 1).getDay() + 6) % 7;
    const dim = new Date(cursor.y, cursor.m + 1, 0).getDate();
    const cells: ({ day: number; recs: FinanzasRecurring[] } | null)[] = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= dim; d++) {
      cells.push({ day: d, recs: activeRecs.filter((r) => occurrencesInMonth(r, cursor.y, cursor.m).includes(d)) });
    }
    return cells;
  }, [cursor, activeRecs]);

  const shiftMonth = (delta: number) => setCursor((c) => {
    const n = c.m + delta;
    return { y: c.y + Math.floor(n / 12), m: ((n % 12) + 12) % 12 };
  });
  const monthLabel = capitalize(new Intl.DateTimeFormat("es-AR", { month: "long", year: "numeric" }).format(new Date(cursor.y, cursor.m, 1)));
  const isThisMonth = cursor.y === today.getFullYear() && cursor.m === today.getMonth();
  const showCatalog = kindFilter !== "income";
  const catalog = showCatalog
    ? GASTOS_FRECUENTES_CATALOG.filter((s) => cat === "todas" || s.category === cat)
    : [];

  const newRecPreset = (day?: number): Partial<RecForm> => {
    const base: Partial<RecForm> = {
      frequency: "monthly",
      day_of_month: day != null ? String(day) : "1",
    };
    // kindFilter manda sobre la categoría (evita abrir ingreso con filtro Gastos fijos + cat ingresos).
    if (kindFilter === "income") {
      return { ...base, kind: "income", category: "ingresos" };
    }
    if (kindFilter === "expense") {
      return {
        ...base,
        kind: "expense",
        category: cat === "todas" || cat === "ingresos" ? "vivienda" : cat,
      };
    }
    // Todas: categoría "ingresos" → cobro; resto → gasto fijo.
    if (cat === "ingresos") {
      return { ...base, kind: "income", category: "ingresos" };
    }
    return {
      ...base,
      kind: "expense",
      category: cat === "todas" ? "vivienda" : cat,
    };
  };

  const openNew = (day?: number) => setForm(emptyRecForm(newRecPreset(day)));

  /** Click en día del calendario: asigna día al form abierto, o abre alta con ese día. */
  const onDayClick = (day: number) => {
    if (form) {
      if (form.frequency === "monthly") {
        setForm({ ...form, day_of_month: String(day), frequency: "monthly" });
      } else {
        setForm({ ...form, next_date: isoOnMonthDay(cursor.y, cursor.m, day) });
      }
      return;
    }
    openNew(day);
  };

  const nextSortOrder = () =>
    allRecs.reduce((max, r) => Math.max(max, Number(r.sort_order) || 0), 0) + 1;

  const persist = async (f: RecForm) => {
    const amt = Number(f.amount);
    if (!f.name.trim()) { toast({ title: "Falta el nombre", variant: "destructive" }); return; }
    if (!Number.isFinite(amt) || amt <= 0) { toast({ title: "Monto inválido", variant: "destructive" }); return; }
    const domRaw = f.frequency === "monthly" ? Math.round(Number(f.day_of_month)) : null;
    const dom = domRaw != null && Number.isInteger(domRaw) && domRaw >= 1 && domRaw <= 31 ? domRaw : null;
    if (f.frequency === "monthly" && dom == null) {
      toast({ title: "Día del mes inválido", description: "Usá un entero 1–31 o clickeá un día en el calendario.", variant: "destructive" });
      return;
    }
    if ((f.frequency === "weekly" || f.frequency === "yearly") && !/^\d{4}-\d{2}-\d{2}/.test(f.next_date || "")) {
      toast({
        title: "Falta la próxima fecha",
        description: "Para semanal/anual elegí un día en el calendario o completá la fecha.",
        variant: "destructive",
      });
      return;
    }
    // Preservar sort_order en edit; en alta usar max+1 (nunca forzar 0 en updates).
    const existing = f.id > 0 ? allRecs.find((r) => r.id === f.id) : undefined;
    const sort_order = existing != null ? (Number(existing.sort_order) || 0) : nextSortOrder();
    const row = {
      id: f.id < 0 ? undefined : f.id, name: f.name.trim(), kind: f.kind, amount: amt, currency: f.currency,
      category: f.category || null, frequency: f.frequency, day_of_month: dom, next_date: f.next_date || null,
      icon: f.icon.trim() || null, active: f.active, note: f.note.trim() || null, sort_order,
      // Alta manual o edición = ya validada por el usuario.
      user_validated: true,
    };
    try {
      await save.mutateAsync({ table: "recurring", rows: [row] });
      toast({ title: f.kind === "income" ? "Ingreso recurrente guardado" : "Gasto fijo guardado" });
      setForm(null);
    } catch (e) {
      toast({ title: "Error al guardar", description: e instanceof Error ? e.message : "Error", variant: "destructive" });
    }
  };
  const toggleActive = async (r: FinanzasRecurring) => {
    try {
      // Preserve pending (null) — pause must not auto-validate seed.
      await save.mutateAsync({
        table: "recurring",
        rows: [{
          ...r,
          active: !r.active,
          user_validated: r.user_validated === true ? true : r.user_validated === false ? false : null,
        }],
      });
      toast({ title: r.active ? "Recurrente pausada" : "Recurrente activada", description: r.name });
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : "Error", variant: "destructive" });
    }
  };
  const remove = async (r: FinanzasRecurring) => {
    const label = r.kind === "income" ? "ingreso recurrente" : "gasto fijo";
    if (!window.confirm(`¿Eliminar ${label} «${r.name}»?\nEsta acción no se puede deshacer.`)) return;
    try {
      await save.mutateAsync({ table: "recurring", rows: [], deleteIds: [r.id] });
      if (form?.id === r.id) setForm(null);
      toast({ title: "Recurrente eliminada" });
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : "Error", variant: "destructive" });
    }
  };

  /** Seed/hardcode: Sí = confirmar recurrente; No = copiar a gasto único en Transacciones y borrar plantilla. */
  const validateRecurring = async (r: FinanzasRecurring, isRecurring: boolean) => {
    try {
      if (isRecurring) {
        await save.mutateAsync({
          table: "recurring",
          rows: [{ ...r, user_validated: true, active: true }],
        });
        toast({
          title: "Validado: recurrente",
          description: `«${r.name}» queda como gasto/ingreso fijo.`,
        });
        return;
      }
      // No → gasto (o ingreso) único en el libro + quitar de recurrentes
      const y = today.getFullYear();
      const m = String(today.getMonth() + 1).padStart(2, "0");
      const d = String(today.getDate()).padStart(2, "0");
      const tx_date = `${y}-${m}-${d}`;
      await save.mutateAsync({
        table: "transactions",
        rows: [{
          tx_date,
          description: r.name,
          kind: r.kind,
          amount: r.amount,
          currency: r.currency,
          category: r.category,
          account: null,
          origin: "manual",
          recurring_id: null,
          note: r.note
            ? `${r.note} · Validado: no recurrente (gasto/ingreso único)`
            : "Validado: no recurrente (gasto/ingreso único desde seed)",
          sort_order: 0,
        }],
      });
      await save.mutateAsync({ table: "recurring", rows: [], deleteIds: [r.id] });
      if (form?.id === r.id) setForm(null);
      toast({
        title: "Validado: es un gasto único",
        description: `«${r.name}» se movió a Transacciones y salió de Recurrentes.`,
      });
    } catch (e) {
      toast({ title: "Error al validar", description: e instanceof Error ? e.message : "Error", variant: "destructive" });
    }
  };

  const newBtnLabel =
    kindFilter === "income" ? "Nuevo ingreso" : kindFilter === "expense" ? "Nuevo gasto fijo" : "Nueva";

  const renderSection = (title: string, rows: FinanzasRecurring[], emptyCta: { label: string; onClick: () => void }) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] uppercase tracking-wide text-white/40">{title} · {rows.length}</div>
        {rows.length === 0 && (
          <button type="button" onClick={emptyCta.onClick} className="text-[11px] font-medium text-violet-300/90 hover:text-violet-200">
            {emptyCta.label}
          </button>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/10 p-5 text-center text-[13px] text-white/35">
          Vacío. {emptyCta.label} o usá el calendario (click en un día).
        </div>
      ) : (
        rows.map((r) => (
          <RecListRow
            key={r.id}
            r={r}
            today={today}
            onEdit={(x) => setForm(recToForm(x))}
            onToggle={toggleActive}
            onRemove={remove}
            onValidate={validateRecurring}
            validating={save.isPending}
          />
        ))
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="max-w-xl text-[13px] text-white/60">
          Ingresos recurrentes y gastos fijos (alquiler, expensas, créditos…). Filas naranjas = seed sin validar: hover y elegí Sí (recurrente) o No (gasto único). Solo super admin.
        </div>
        <div className="flex items-center gap-2">
          <CurrencyToggle value={display} onChange={setDisplay} />
          <Button size="sm" onClick={() => (form ? setForm(null) : openNew())}>
            <Plus className="mr-1 h-3.5 w-3.5" />{form ? "Cerrar form" : newBtnLabel}
          </Button>
        </div>
      </div>

      {pendingValidation.length > 0 && (
        <div className="rounded-xl border border-orange-500/40 bg-orange-500/[0.1] px-4 py-3 text-[13px] text-orange-100">
          <div className="flex flex-wrap items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange-300" />
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-orange-50">
                {pendingValidation.length} fila{pendingValidation.length === 1 ? "" : "s"} del seed sin validar
              </div>
              <p className="mt-0.5 text-[12px] text-orange-100/75">
                Datos hardcodeados (alquiler, expensas, NX, Ualá…). Pasá el mouse sobre la fila naranja y elegí{" "}
                <strong className="text-orange-50">Sí · recurrente</strong> o{" "}
                <strong className="text-orange-50">No · es un gasto</strong> (lo mueve a Transacciones como único).
                Hasta validar no cuentan en KPIs ni en el calendario.
              </p>
              <p className="mt-1 text-[11px] text-orange-200/60">
                Pendientes: {pendingValidation.map((x) => x.name).join(" · ")}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Resumen Oruon · KPIs + utilization + próximos (primitives admin-wide) */}
      <AdminKpiGrid cols={4}>
        <AdminStatCard
          title="Ingresos / mes"
          value={fmtMoney(totals.inc, display)}
          icon={ArrowUpRight}
          hint={`${totals.nInc} cobro${totals.nInc === 1 ? "" : "s"} activo${totals.nInc === 1 ? "" : "s"}`}
          accent={totals.inc > 0}
          className="border-emerald-500/15"
        />
        <AdminStatCard
          title="Gastos fijos / mes"
          value={fmtMoney(totals.exp, display)}
          icon={ArrowDownLeft}
          hint={`${totals.nExp} gasto${totals.nExp === 1 ? "" : "s"} activo${totals.nExp === 1 ? "" : "s"}`}
          className="border-red-500/15"
        />
        <AdminStatCard
          title="Neto / mes"
          value={fmtMoney(totals.net, display)}
          icon={Repeat}
          hint={totals.net >= 0 ? "Ingresos ≥ gastos fijos" : "Gastos superan ingresos"}
          className={totals.net >= 0 ? "border-emerald-500/15" : "border-red-500/20"}
        />
        <AdminStatCard
          title="Activas en calendario"
          value={String(totals.nActive)}
          icon={Calendar}
          hint="Solo recurrentes activas"
        />
      </AdminKpiGrid>

      <AdminUtilizationBar
        label={totals.inc > 0 ? "Gastos fijos vs ingresos recurrentes" : "Gastos fijos (sin ingresos recurrentes base)"}
        value={totals.exp}
        max={totals.inc > 0 ? totals.inc : 0}
        higherIsBetter={false}
        hint={
          totals.inc > 0
            ? "Comprometido del mes en recurrentes activas (manual). MP real sigue en Transacciones."
            : "Agregá un ingreso recurrente para ver % de cobertura. Base = 0 → sin ratio."
        }
        formatValue={(n) => fmtMoney(n, display)}
      />

      <div className="space-y-2">
        <div className="text-[11px] uppercase tracking-wide text-white/40">Próximos vencimientos</div>
        <AdminUpcomingList
          items={upcomingItems}
          emptyLabel="No hay recurrentes activas con fecha — activá o asigná un día en el calendario."
          max={8}
          onItemClick={(item) => {
            const rec = allRecs.find((r) => r.id === item.id);
            if (rec) setForm(recToForm(rec));
          }}
        />
      </div>

      {/* Filtro por tipo (primario) */}
      <div className="flex flex-wrap gap-1.5">
        {KIND_FILTERS.map((k) => (
          <button key={k.key} type="button" onClick={() => setKindFilter(k.key)}
            className={cn("rounded-full border px-3 py-1.5 text-[12px] font-semibold transition",
              kindFilter === k.key ? "border-violet-500/40 bg-violet-500/15 text-violet-100" : "border-white/10 text-white/50 hover:text-white/80")}>
            {k.emoji} {k.label}
          </button>
        ))}
      </div>

      {/* Filtro por categoría (secundario) */}
      <div className="flex flex-wrap gap-1.5">
        {RECURRING_CATEGORIES.map((c) => (
          <button key={c.key} type="button" onClick={() => setCat(c.key)}
            className={cn("rounded-full border px-2.5 py-1 text-[12px] font-medium transition",
              cat === c.key ? "border-white/25 bg-white/10 text-white/90" : "border-white/10 text-white/45 hover:text-white/75")}>
            {c.emoji} {c.label}
          </button>
        ))}
      </div>

      {form && <RecFormCard form={form} setForm={setForm} onSave={persist} onCancel={() => setForm(null)} saving={save.isPending} />}

      {/* Calendario interactivo */}
      <Card className="border-white/10 bg-white/[0.02]">
        <div className="flex flex-col items-center gap-1 px-3 py-2 sm:flex-row sm:justify-center sm:gap-3">
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => shiftMonth(-1)} className="rounded-md p-1 text-white/50 hover:bg-white/10 hover:text-white"><ChevronLeft className="h-4 w-4" /></button>
            <span className="flex items-center gap-1.5 text-[13px] font-semibold text-white/80"><Calendar className="h-3.5 w-3.5 text-white/40" />{monthLabel}</span>
            <button type="button" onClick={() => shiftMonth(1)} className="rounded-md p-1 text-white/50 hover:bg-white/10 hover:text-white"><ChevronRight className="h-4 w-4" /></button>
          </div>
          <div className="text-[10px] text-white/35">
            {form ? "Click en un día para asignar la fecha del form" : "Click en un día para alta · click en chip para editar"}
          </div>
        </div>
        <div className="grid grid-cols-7 gap-px border-t border-white/10 bg-white/[0.04] text-center text-[10px] uppercase text-white/35">
          {WEEKDAYS.map((w) => <div key={w} className="py-1.5">{w}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-px bg-white/[0.04]">
          {cal.map((cell, i) => {
            const isToday = cell != null && isThisMonth && cell.day === today.getDate();
            const formDay =
              form?.frequency === "monthly" && form.day_of_month
                ? Number(form.day_of_month)
                : null;
            const isFormTarget = cell != null && formDay != null && cell.day === formDay;
            // Celda = contenedor (no role=button) para no anidar botones; el día y los chips son los controles.
            return (
              <div
                key={i}
                className={cn(
                  "min-h-[68px] bg-[#0d0d0f] p-1 text-left",
                  cell == null && "pointer-events-none opacity-0",
                  isFormTarget && "ring-1 ring-inset ring-violet-400/50",
                )}
              >
                {cell && (
                  <>
                    <div className="mb-0.5 flex justify-end">
                      <button
                        type="button"
                        title={form ? `Asignar día ${cell.day} al form` : `Nueva recurrente el día ${cell.day}`}
                        onClick={() => onDayClick(cell.day)}
                        className={cn(
                          "rounded px-1 text-[11px] tabular-nums transition hover:bg-white/10",
                          isToday ? "font-bold text-violet-300" : "text-white/40 hover:text-white/70",
                        )}
                      >
                        {cell.day}
                      </button>
                    </div>
                    <div className="space-y-0.5">
                      {cell.recs.slice(0, 3).map((r) => {
                        const amt = inCurrency(r.amount, r.currency, display, rate) ?? r.amount;
                        return (
                          <button
                            key={r.id}
                            type="button"
                            title={`${r.name} — ${fmtMoney(r.amount, r.currency)} · click para editar`}
                            onClick={() => setForm(recToForm(r))}
                            className={cn(
                              "block w-full truncate rounded px-1 py-0.5 text-left text-[9px] font-semibold leading-tight",
                              r.kind === "income" ? "bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25" : "bg-red-500/12 text-red-200 hover:bg-red-500/20",
                            )}
                          >
                            {r.icon ? `${r.icon} ` : ""}{fmtCompact(amt)}
                          </button>
                        );
                      })}
                      {cell.recs.length > 3 && <div className="px-1 text-[9px] text-white/35">+{cell.recs.length - 3}</div>}
                      {cell.recs.length === 0 && (
                        <button
                          type="button"
                          title={`Agregar en día ${cell.day}`}
                          onClick={() => onDayClick(cell.day)}
                          className="block w-full rounded px-1 py-2 text-[9px] text-white/20 hover:bg-white/[0.04] hover:text-white/40"
                        >
                          +
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Listas por tipo */}
      {kindFilter === "all" && (
        <div className="space-y-5">
          {renderSection("Ingresos recurrentes", incomeRecs, {
            label: "Agregar cobro fijo",
            onClick: () => setForm(emptyRecForm({ kind: "income", category: "ingresos", frequency: "monthly", day_of_month: "1" })),
          })}
          {renderSection("Gastos fijos", expenseRecs, {
            label: "Agregar gasto fijo",
            onClick: () => setForm(emptyRecForm({ kind: "expense", category: cat === "todas" || cat === "ingresos" ? "vivienda" : cat, frequency: "monthly", day_of_month: "1" })),
          })}
        </div>
      )}
      {kindFilter === "income" && renderSection(
        `Ingresos recurrentes${cat !== "todas" ? ` · ${recurringCategoryMeta(cat).label}` : ""}`,
        incomeRecs,
        {
          label: "Agregar cobro fijo",
          onClick: () => setForm(emptyRecForm({ kind: "income", category: "ingresos", frequency: "monthly", day_of_month: "1" })),
        },
      )}
      {kindFilter === "expense" && renderSection(
        `Gastos fijos${cat !== "todas" ? ` · ${recurringCategoryMeta(cat).label}` : ""}`,
        expenseRecs,
        {
          label: "Agregar gasto fijo",
          onClick: () => setForm(emptyRecForm({ kind: "expense", category: cat === "todas" || cat === "ingresos" ? "vivienda" : cat, frequency: "monthly", day_of_month: "1" })),
        },
      )}

      {/* Catálogo de gastos fijos frecuentes (no en vista solo ingresos) */}
      {catalog.length > 0 && (
        <div className="space-y-2">
          <div className="text-[11px] uppercase tracking-wide text-white/40">Agregar gasto fijo frecuente</div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {catalog.map((s) => (
              <button key={s.name} type="button"
                onClick={() => setForm(emptyRecForm({
                  name: s.name, amount: String(s.amount), currency: s.currency, category: s.category,
                  kind: "expense", icon: s.icon, frequency: "monthly", day_of_month: "1",
                }))}
                className="flex items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.02] p-2.5 text-left transition hover:border-white/25 hover:bg-white/[0.05]">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/[0.06] text-lg">{s.icon}</div>
                <div className="min-w-0">
                  <div className="truncate text-[12px] font-semibold text-white/85">{s.name}</div>
                  <div className="text-[11px] text-white/45">{fmtMoney(s.amount, s.currency)}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────────
export default function FinanzasPersonalesAdmin() {
  const { isLoaded: claimsLoaded } = useUser();
  const isSuperAdmin = useIsSuperAdmin();
  const { data, isLoading, isError, error, isFetching, dataUpdatedAt, refetch, isRefetchError } = useFinanzasPersonales({
    enabled: claimsLoaded && isSuperAdmin,
  });
  const save = useSaveFinanzas();
  const refreshFresh = useRefreshFinanzas();
  const { toast } = useToast();
  // "Hoy" se refresca al enfocar la ventana y cada minuto (no congelar al mount).
  const [today, setToday] = useState(() => new Date());
  useEffect(() => {
    const tick = () => setToday(new Date());
    window.addEventListener("focus", tick);
    const id = window.setInterval(tick, 60_000);
    return () => {
      window.removeEventListener("focus", tick);
      window.clearInterval(id);
    };
  }, []);
  const mesTab = monthNameEs(today, 0);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [removed, setRemoved] = useState({ items: [] as number[], loan: [] as number[], history: [] as number[] });
  const [savingAll, setSavingAll] = useState(false);

  // Esperar claims Clerk: evita bounce falso a /admin antes de saber si es super-admin.
  if (!claimsLoaded) {
    return (
      <AdminPageShell width="lg" className="finanzas-panel">
        <div className="flex items-center justify-center py-16 text-white/40">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />Verificando acceso…
        </div>
      </AdminPageShell>
    );
  }
  if (!isSuperAdmin) return <Navigate to="/admin" replace />;

  const defaultMeta = (): FinanzasMeta => ({
    id: 1, person_name: "", gestor: "", alert_text: "", alert_subtext: "",
    loan_capital: null, loan_neto: null, loan_total: null, loan_tna: "",
    loan_active: true, loan_first_date: null, usd_ars_rate: null,
  });

  const seedDraftFrom = (src: FinanzasData): Draft => ({
    meta: src.meta ?? defaultMeta(),
    items: structuredClone(src.items),
    loan: structuredClone(src.loan),
    history: structuredClone(src.history),
  });

  const enterEdit = () => {
    if (!data) return;
    setRemoved({ items: [], loan: [], history: [] });
    setDraft(seedDraftFrom(data));
    setEditing(true);
  };
  const cancelEdit = () => {
    setEditing(false);
    setDraft(null);
    setRemoved({ items: [], loan: [], history: [] });
  };

  /**
   * Guardado multi-tabla: rehidrata SOLO la tabla del step (no pisar draft de otras
   * tablas aún no guardadas). Limpia deleteIds ya aplicados → reintentar no duplica INSERTs.
   */
  const handleSave = async () => {
    if (!draft || savingAll) return;
    setSavingAll(true);
    let working = draft;
    let rem = removed;
    const clean = <T extends { id: number }>(rows: T[]) =>
      rows.map((r) => (r.id < 0 ? { ...r, id: undefined } : r));
    const done: string[] = [];
    try {
      const steps: { table: "meta" | "items" | "loan" | "history"; run: () => Promise<FinanzasData> }[] = [
        {
          table: "meta",
          run: () => save.mutateAsync({ table: "meta", rows: [working.meta] }),
        },
        {
          table: "items",
          run: () => save.mutateAsync({ table: "items", rows: clean(working.items), deleteIds: rem.items }),
        },
        {
          table: "loan",
          run: () => save.mutateAsync({ table: "loan", rows: clean(working.loan), deleteIds: rem.loan }),
        },
        {
          table: "history",
          run: () => save.mutateAsync({ table: "history", rows: clean(working.history), deleteIds: rem.history }),
        },
      ];
      for (const step of steps) {
        try {
          const fresh = await step.run();
          done.push(step.table);
          const s = seedDraftFrom(fresh);
          // Partial rehydrate — keep unsaved sibling tables from working draft.
          if (step.table === "meta") working = { ...working, meta: s.meta };
          else if (step.table === "items") working = { ...working, items: s.items };
          else if (step.table === "loan") working = { ...working, loan: s.loan };
          else if (step.table === "history") working = { ...working, history: s.history };
          setDraft(working);
          if (step.table === "items") rem = { ...rem, items: [] };
          if (step.table === "loan") rem = { ...rem, loan: [] };
          if (step.table === "history") rem = { ...rem, history: [] };
          setRemoved(rem);
        } catch (err) {
          toast({
            title: `Error al guardar «${step.table}»`,
            description: `${err instanceof Error ? err.message : "Error"}${
              done.length ? ` · Ya guardado: ${done.join(", ")}. El borrador se actualizó con lo guardado; reintentá el resto.` : ""
            }`,
            variant: "destructive",
          });
          void refetch();
          return;
        }
      }
      toast({ title: "Finanzas actualizadas", description: "Meta, items, préstamo e historial." });
      cancelEdit();
    } finally {
      setSavingAll(false);
    }
  };

  const handleRefresh = () => {
    // ?fresh=1 relee Postgres (invalida cache de feed). No hit a MercadoPago API.
    refreshFresh.mutate(undefined, {
      onSuccess: () => {
        toast({
          title: "Datos desde base de datos",
          description: "Libro + histórico MercadoPago releídos de Postgres (sin API externa).",
        });
      },
      onError: (e) => {
        toast({
          title: "No se pudo actualizar",
          description: e instanceof Error ? e.message : "Error",
          variant: "destructive",
        });
      },
    });
  };

  const refreshing = isFetching || refreshFresh.isPending;

  return (
    <AdminPageShell width="lg" className="finanzas-panel">
      <AdminPageHeader
        title="Oruon · Finanzas personales"
        description={`Ana · hoy ${formatTodayEsAR(today)} · super admin · vencimientos y feed real al día · Postgres personal-finance`}
       
        actions={
          editing ? (
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={cancelEdit} disabled={savingAll}><X className="mr-1 h-3.5 w-3.5" />Cancelar</Button>
              <Button size="sm" onClick={handleSave} disabled={savingAll || !draft}>
                {savingAll ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-2 h-3.5 w-3.5" />}Guardar
              </Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleRefresh} disabled={refreshing || (!data && !isError)} title="Releer Postgres (histórico MercadoPago + libro)">
                <RefreshCw className={cn("mr-1.5 h-3.5 w-3.5", refreshing && "animate-spin")} />Actualizar DB
              </Button>
              <Button size="sm" variant="outline" onClick={enterEdit} disabled={!data}><Pencil className="mr-1.5 h-3.5 w-3.5" />Editar</Button>
            </div>
          )
        }
      />

      {isLoading && <div className="flex items-center justify-center py-16 text-white/40"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Cargando finanzas…</div>}
      {isError && !data && (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-red-500/30 bg-red-500/[0.06] p-4 text-sm text-red-300 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>{error instanceof Error ? error.message : "Error al cargar"}</span>
          </div>
          <Button size="sm" variant="outline" onClick={handleRefresh} disabled={refreshing} className="border-red-500/30 text-red-200">
            <RefreshCw className={cn("mr-1.5 h-3.5 w-3.5", refreshing && "animate-spin")} />Reintentar
          </Button>
        </div>
      )}

      {!isLoading && data && (
        editing && draft ? (
          <EditView draft={draft} setDraft={setDraft} removed={removed} setRemoved={setRemoved} today={today} />
        ) : (
          <>
            <FinanzasActualidadBar
              data={data}
              dataUpdatedAt={dataUpdatedAt}
              isFetching={refreshing}
              onRefresh={handleRefresh}
            />

            {(isRefetchError || (isError && data)) && (
              <div className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/[0.08] px-3 py-2 text-[12px] text-amber-100/90">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                No se pudo refrescar el panel — se muestra la última carga.{" "}
                <button type="button" className="underline underline-offset-2" onClick={handleRefresh}>
                  Reintentar
                </button>
              </div>
            )}
            {data && data.real == null && !isLoading && (
              <div className="flex items-center gap-2 rounded-lg border border-sky-500/25 bg-sky-500/[0.06] px-3 py-2 text-[12px] text-sky-100/85">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                Feed MercadoPago no disponible (error o tablas). El libro manual y el diario API siguen activos.{" "}
                <button type="button" className="underline underline-offset-2" onClick={handleRefresh}>
                  Sync
                </button>
              </div>
            )}

            {data.meta?.alert_text && (
              <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-emerald-500/25 bg-gradient-to-br from-emerald-500/[0.12] to-transparent p-4 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15">
                    <CheckCircle2 className="h-[18px] w-[18px] text-emerald-400" />
                  </div>
                  <div>
                    <div className="text-[13px] font-semibold text-emerald-300">{data.meta.alert_text}</div>
                    {data.meta.alert_subtext && <div className="mt-0.5 text-[11px] text-emerald-200/60">{data.meta.alert_subtext}</div>}
                  </div>
                </div>
                {(() => {
                  const next = itemsOf(data, "julio")
                    .filter((x) => x.status !== "paid" && x.status !== "cancelled" && x.due_date)
                    .sort((a, b) => (a.due_date ?? "").localeCompare(b.due_date ?? ""))[0];
                  const d = next ? daysUntil(next.due_date, today) : null;
                  return next ? (
                    <div className="text-right">
                      <div className="text-[10px] text-white/40"><Clock className="mr-1 inline h-3 w-3" />Próximo pago</div>
                      <div className="proportional-nums tracking-tight text-[14px] font-bold text-amber-400">{next.due_label ?? next.due_date} — {fmt(next.amount)}</div>
                      <div className="text-[9px] text-white/40">{next.label} · {d != null ? (d <= 0 ? "vencido" : `${d} días`) : ""}</div>
                    </div>
                  ) : null;
                })()}
              </div>
            )}

            <KpiCards data={data} today={today} />
            <PlanCoverageBar data={data} />

            <Tabs defaultValue="transacciones">
              <TabsList className="flex h-auto w-full gap-1 overflow-x-auto rounded-2xl bg-white/[0.03] p-1.5 lg:grid lg:grid-cols-7 lg:overflow-visible">
                {([
                  ["transacciones", "💸", "Transacciones"],
                  ["trazabilidad", "🔗", "Trazabilidad"],
                  ["recurrentes", "🔁", "Recurrentes"],
                  ["evolucion", "📈", "Evolución"],
                  ["lista", "🔍", "Lista"],
                  ["julio", "📅", capitalize(mesTab)],
                  ["deuda", "💡", "Deuda"],
                ] as const).map(([v, e, l]) => (
                  <TabsTrigger key={v} value={v} className="shrink-0 flex-col gap-0.5 rounded-xl px-3 py-2 text-[11px] text-white/55 data-[state=active]:bg-white/10 data-[state=active]:text-white data-[state=active]:shadow-sm sm:flex-row sm:gap-1.5 lg:shrink">
                    <span className="text-base">{e}</span> <span className="hidden sm:inline">{l}</span><span className="text-[10px] sm:hidden">{l}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
              <TabsContent value="transacciones" className="mt-4">
                <TransaccionesTab
                  data={data}
                  today={today}
                  isFetching={refreshing}
                  onRefresh={handleRefresh}
                />
              </TabsContent>
              <TabsContent value="trazabilidad" className="mt-4">
                <TrazabilidadTab
                  data={data}
                  isFetching={refreshing}
                  onRefresh={handleRefresh}
                />
              </TabsContent>
              <TabsContent value="recurrentes" className="mt-4"><RecurrentesTab data={data} today={today} /></TabsContent>
              <TabsContent value="evolucion" className="mt-4"><EvolucionTab data={data} today={today} /></TabsContent>
              <TabsContent value="lista" className="mt-4"><ListaTab data={data} today={today} /></TabsContent>
              <TabsContent value="julio" className="mt-4"><JulioTab data={data} today={today} /></TabsContent>
              <TabsContent value="deuda" className="mt-4"><DeudaTab data={data} /></TabsContent>
            </Tabs>
          </>
        )
      )}
    </AdminPageShell>
  );
}
